import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { PostRefineView, CarouselView, ThreadView } from './SocialStudioPostViews';
import { TrendReportView, CreatorAnalysisView, PerformanceTimeline, Stat, InsightsView } from './SocialStudioRoundViews';
import { UnifiedLibrary } from './SocialStudioLibraryView';
import { BrandReferenceLibrary } from './BrandReferenceLibrary';
import { ThemeBankEditor } from './ThemeBankEditor';
import { DFYPlanGenerator } from './DFYPlanGenerator';
import StudioPlanBar from './StudioPlanBar';
import StudioSuiteNav from '../../components/studio/StudioSuiteNav';
import { ViralFormatPicker, HookLibraryPicker, ReelChecklistCard, GenerationProgress } from './SocialStudioViralTools';
import BrandProfileManager from '../../components/studio/BrandProfileManager';
import { toTeleprompterText, downloadTxt } from '../../lib/teleprompterTxt';
import { groupedProfileOptions } from '../../components/studio/brandProfileOptions';
import {
  Sparkles,
  Mic,
  MicOff,
  Copy,
  RefreshCw,
  Save,
  Trash2,
  Wand2,
  ListChecks,
  Library,
  UserSquare2,
  Plus,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Zap,
  Heart,
  Gauge,
  Camera,
  BookOpen,
  Pencil,
  TrendingUp,
  Layers,
  Users,
  Calendar,
  X,
  Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

// The access-token cookie lives only 15 min; the refresh-token cookie lives 7-30 days.
// Transparently refresh on any 401 and retry the original request ONCE, so long-lived
// studio sessions never surface a spurious "Not authenticated" error on any tab.
let _refreshPromise = null;
const _GEN_URL_RE = /\/(generate-script|posts\/generate|carousels\/generate|threads\/generate|engagement\/generate|community\/generate|convert-to-video|items\/[^/]+\/generate-script)\b/;
api.interceptors.response.use(
  (resp) => {
    // Refresh the daily-quota bar after any successful new-creation generation.
    try {
      if (resp?.config?.url && _GEN_URL_RE.test(resp.config.url)) {
        window.dispatchEvent(new CustomEvent('studio-usage-refresh'));
      }
    } catch (_e) { /* noop */ }
    return resp;
  },
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isRefreshCall = original?.url?.includes('/api/auth/refresh');
    if (status === 401 && original && !original._retry && !isRefreshCall) {
      original._retry = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = axios
            .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { _refreshPromise = null; });
        }
        await _refreshPromise;
        return api(original);
      } catch (_e) {
        return Promise.reject(error);
      }
    }
    // Social Studio daily generation cap reached: surface a friendly upgrade/reset
    // toast centrally and normalize the error detail to a readable string so any
    // downstream `toast.error(detail)` never renders "[object Object]".
    const detail = error.response?.data?.detail;
    if (status === 403 && detail && typeof detail === 'object' && (detail.error === 'studio_limit_reached' || detail.error === 'studio_upgrade_required')) {
      try { window.dispatchEvent(new CustomEvent('studio-usage-refresh')); } catch (_e) { /* noop */ }
      try { window.dispatchEvent(new CustomEvent('studio-limit-hit', { detail })); } catch (_e) { /* noop */ }
      const opts = detail.can_upgrade
        ? { action: { label: 'Upgrade', onClick: () => { try { window.dispatchEvent(new CustomEvent('studio-upgrade-request')); } catch (_e) { /* noop */ } } } }
        : undefined;
      try { toast.error(detail.message, opts); } catch (_e) { /* noop */ }
      error.response.data.detail = detail.message;
    }
    return Promise.reject(error);
  }
);

const PLATFORMS = [
  { key: 'all_reels', label: 'All Reels', color: '#1D4ED8' },
  { key: 'tiktok', label: 'TikTok', color: '#000' },
  { key: 'instagram_reels', label: 'Instagram Reels', color: '#E1306C' },
  { key: 'facebook_reels', label: 'Facebook Reels', color: '#1877F2' },
  { key: 'youtube_shorts', label: 'YouTube Shorts', color: '#FF0000' },
  { key: 'snapchat', label: 'Snapchat', color: '#FFFC00' },
  { key: 'x_twitter', label: 'X / Twitter Video', color: '#000000' },
];

// Single-platform pickers (remix / convert / repurpose) should not offer the universal "All Reels".
const SINGLE_PLATFORMS = PLATFORMS.filter((p) => p.key !== 'all_reels');

const TONES = [
  'friendly-conversational', 'conversational', 'bold', 'compassionate', 'urgent', 'educational', 'motivational',
  'funny', 'faith-based', 'luxury', 'direct', 'storytelling', 'controversial',
  'warm', 'professional', 'street-level', 'high-authority',
];

const HOOK_STYLES = [
  { key: 'fear', label: 'Fear-based' },
  { key: 'curiosity', label: 'Curiosity' },
  { key: 'authority', label: 'Authority' },
  { key: 'contrarian', label: 'Contrarian' },
  { key: 'story', label: 'Story' },
  { key: 'pain', label: 'Pain-point' },
  { key: 'mistake', label: 'Mistake' },
  { key: 'urgency', label: 'Urgency' },
  { key: 'myth', label: 'Myth-busting' },
  { key: 'nobody-talks', label: 'Nobody Talks About This' },
  { key: 'wrong', label: "You're Doing This Wrong" },
  { key: 'wish-i-knew', label: 'I Wish I Knew This Sooner' },
  { key: 'confession', label: 'Personal Confession' },
  { key: 'question', label: 'Question' },
  { key: 'stat', label: 'Shock / Statistic' },
];

const CTA_INTENTS = [
  { key: 'comment', label: 'Drive comments' },
  { key: 'follow', label: 'Get the follow' },
  { key: 'dm', label: 'Get a DM' },
  { key: 'save', label: 'Get saves' },
  { key: 'link', label: 'Link clicks' },
  { key: 'tag', label: 'Tag a friend' },
  { key: 'book', label: 'Book a call' },
];

const TABS = [
  { key: 'generator', label: 'Script Generator', icon: Sparkles },
  { key: 'refine', label: 'Refine & Variants', icon: Wand2 },
  { key: 'hook_engine', label: 'Hook Engine', icon: Zap },
  { key: 'planner', label: 'Content Planner', icon: Camera },
  { key: 'repurpose', label: 'Repurpose', icon: TrendingUp },
  { key: 'trends', label: 'Trends', icon: TrendingUp },
  { key: 'creator', label: 'Creator Analysis', icon: UserSquare2 },
  { key: 'performance', label: 'Performance', icon: Gauge },
  { key: 'post_studio', label: 'Post Studio', icon: BookOpen },
  { key: 'ideas', label: 'Idea & Hook Lab', icon: ListChecks },
  { key: 'library', label: 'Library', icon: Library },
  { key: 'brands', label: 'Brand Profiles', icon: UserSquare2 },
];

// Studio mode dictates which top-tab strip is shown. The user's requirement:
// IDENTICAL 12-tab order in both Video and Post Studio. Carousels, Threads,
// Engagement, Community were removed per user request (post-format-only, don't
// apply to short-form video). Repurpose and Scheduler are kept in their original
// studio (Repurpose -> video, Scheduler -> post) but placed at the SAME slot
// position so the visual order matches.
const UNIFIED_TABS_BLUEPRINT = [
  { slot: 1,  label_video: 'Script Generator', label_post: 'Post Generator', icon: Sparkles,    video_key: 'generator',   post_sub: 'generator' },
  { slot: 2,  label_video: 'Refine Script',    label_post: 'Refine Post',    icon: Wand2,       video_key: 'refine',      post_sub: 'refine' },
  { slot: 3,  label_video: 'Remix',            label_post: 'Remix',          icon: Wand2,       video_key: 'video_remix', post_sub: 'remix' },
  { slot: 4,  label_video: 'Repurpose',        label_post: 'Scheduler',      icon_video: TrendingUp, icon_post: Calendar, video_key: 'repurpose',   post_sub: 'scheduler' },
  { slot: 5,  label_video: 'Hook Engine',      label_post: 'Hook Engine',    icon: Zap,         video_key: 'hook_engine', post_key: 'hook_engine' },
  { slot: 6,  label_video: 'Content Planner',  label_post: 'Content Planner',icon: Camera,      video_key: 'planner',     post_key: 'planner' },
  { slot: 7,  label_video: 'Trends',           label_post: 'Trends',         icon: TrendingUp,  video_key: 'trends',      post_key: 'trends' },
  { slot: 8,  label_video: 'Creator Analysis', label_post: 'Creator Analysis', icon: UserSquare2, video_key: 'creator',   post_key: 'creator' },
  { slot: 9,  label_video: 'Performance',      label_post: 'Performance',    icon: Gauge,       video_key: 'performance', post_key: 'performance' },
  { slot: 10, label_video: 'Idea & Hook Lab',  label_post: 'Idea & Hook Lab',icon: ListChecks,  video_key: 'ideas',       post_key: 'ideas' },
  { slot: 11, label_video: 'Library',          label_post: 'Library',        icon: Library,     video_key: 'library',     post_sub: 'library' },
  { slot: 12, label_video: 'Brand Profiles',   label_post: 'Brand Profiles', icon: UserSquare2, video_key: 'brands',      post_key: 'brands' },
];

const VIDEO_TABS = UNIFIED_TABS_BLUEPRINT.map((t) => ({
  key: t.video_key,
  label: t.label_video,
  icon: t.icon_video || t.icon,
}));

const POST_TABS = UNIFIED_TABS_BLUEPRINT.map((t) => ({
  key: t.post_sub ? `post:${t.post_sub}` : t.post_key,
  label: t.label_post,
  icon: t.icon_post || t.icon,
  sub: t.post_sub,
}));


// ----- Voice Input Hook (Web Speech API) -----
function useVoiceInput(onTranscript) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice input not supported in this browser. Try Chrome.');
      return;
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let final = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += transcript + ' ';
        else interim += transcript;
      }
      onTranscript(final + interim);
    };
    recognition.onerror = (e) => {
      toast.error('Voice error: ' + e.error);
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  };

  const stop = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  return { listening, start, stop };
}

// ----- Copy to clipboard utility -----
const copy = (text) => {
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Copy failed'),
  );
};

// ===== Main Component =====
export default function SocialStudio({ embedded = false }) {
  const [tab, setTab] = useState('generator');
  const [studioMode, setStudioMode] = useState('video'); // 'video' | 'post'
  const [limitNudge, setLimitNudge] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [activeScript, setActiveScript] = useState(null);

  // ===== Generator state =====
  const [platform, setPlatform] = useState('all_reels');
  const [profileId, setProfileId] = useState('');
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('drive engagement + capture leads');
  const [extraContext, setExtraContext] = useState('');
  const [variants, setVariants] = useState(1);
  const [selectedTones, setSelectedTones] = useState([]);
  const [contentPillar, setContentPillar] = useState('');
  const [generating, setGenerating] = useState(false);
  // Per-action transform locks: allow several DIFFERENT transforms to run at once,
  // but lock each individual action against double-clicks until it resolves.
  const [pending, setPending] = useState({});

  // ===== Proven Viral Templates (format picker) =====
  const [viralFormats, setViralFormats] = useState([]);
  const [formatCategory, setFormatCategory] = useState('');
  const [formatSubtype, setFormatSubtype] = useState('');
  const [formatFaceless, setFormatFaceless] = useState(false);
  const [formatTextOnScreen, setFormatTextOnScreen] = useState(false);
  // Hook Engine format selection
  const [heFormat, setHeFormat] = useState({ category: '', subtype: '', faceless: false, textOnScreen: false });

  // ===== Idea & Hook Lab =====
  const [hookForm, setHookForm] = useState({ platform: 'tiktok', niche: '', audience: '', topic: '', count: 8 });
  const [hooks, setHooks] = useState([]);
  const [hooksLoading, setHooksLoading] = useState(false);

  const [ideaForm, setIdeaForm] = useState({ niche: '', audience: '', count: 10 });
  const [ideas, setIdeas] = useState([]);
  const [ideasLoading, setIdeasLoading] = useState(false);

  // ===== Refine state =====
  const [refineFeedback, setRefineFeedback] = useState('');
  const [refining, setRefining] = useState(false);
  const [hookStyle, setHookStyle] = useState('curiosity');
  const [ctaIntent, setCtaIntent] = useState('comment');
  const [humanizeLevel, setHumanizeLevel] = useState(2);
  const [shareUrl, setShareUrl] = useState('');

  // ===== Smart Fill (idea-only generator) =====
  const [rawIdea, setRawIdea] = useState('');
  const [smartFilling, setSmartFilling] = useState(false);
  const [smartFillResult, setSmartFillResult] = useState(null);

  // ===== Hook Engine =====
  const [hookEngineForm, setHookEngineForm] = useState({ platform: 'tiktok', niche: '', audience: '', topic: '', per_category: 2 });
  const [hookEngineCategories, setHookEngineCategories] = useState([]);
  const [hookEngineSelected, setHookEngineSelected] = useState([]);
  const [hookEngineResults, setHookEngineResults] = useState([]);
  const [hookEngineLoading, setHookEngineLoading] = useState(false);
  const [hookScoreText, setHookScoreText] = useState('');
  const [hookScoreResult, setHookScoreResult] = useState(null);
  const [hookScoring, setHookScoring] = useState(false);

  // ===== Planner =====
  const [planForm, setPlanForm] = useState({ profile_id: '', name: '', days: 7, posts_per_day: 1, extra_notes: '' });
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // ===== Repurpose =====
  const [repurposeForm, setRepurposeForm] = useState({ source_text: '', source_type: 'long_form', count: 5, profile_id: '', extra_notes: '' });
  const [repurposeClips, setRepurposeClips] = useState([]);
  const [repurposeLoading, setRepurposeLoading] = useState(false);

  // ===== Round 3: Trends =====
  const [trendsForm, setTrendsForm] = useState({ niche: '', audience: '', focus: '', competitor_handles: '', manual_paste: '' });
  const [trends, setTrends] = useState([]);
  const [activeTrend, setActiveTrend] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);

  // ===== Round 3: Creator Analysis =====
  const [creatorForm, setCreatorForm] = useState({ competitor_handle: '', competitor_content: '', your_profile_id: '', notes: '' });
  const [analyses, setAnalyses] = useState([]);
  const [activeAnalysis, setActiveAnalysis] = useState(null);
  const [creatorLoading, setCreatorLoading] = useState(false);

  // ===== Round 3: Performance =====
  const [perfForm, setPerfForm] = useState({ views: 0, likes: 0, comments: 0, shares: 0, saves: 0, follows_gained: 0, link_clicks: 0, posted_at: '', notes: '' });
  const [perfMetrics, setPerfMetrics] = useState([]);
  const [perfTotals, setPerfTotals] = useState({ views: 0, engagement: 0, avg_engagement_rate: 0 });
  const [perfSummary, setPerfSummary] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);

  // ===== Library filters =====
  const [libFilters, setLibFilters] = useState({ platform: '', status: '', profile_id: '', q: '' });

  // ===== Round 4: AI Insights =====
  const [insights, setInsights] = useState([]);
  const [activeInsight, setActiveInsight] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightFocus, setInsightFocus] = useState('');

  // ===== Round 5A: Post Studio =====
  const [postMeta, setPostMeta] = useState({ platforms: [], post_types: [], frameworks: [], emotional_targets: [] });
  const [postSubTab, setPostSubTab] = useState('generator'); // generator | refine | carousels | threads | engagement | community | library
  const [postForm, setPostForm] = useState({
    platform: 'facebook', profile_id: '', post_type: 'storytelling', framework: '',
    topic: '', audience: '', niche: '', tones: [], emotional_goal: 'relatability',
    cta_goal: '', content_pillar: '', urgency: 'medium', emoji_level: '',
    hashtag_level: 'auto', length: 'medium', extra_context: '',
  });
  const [posts, setPosts] = useState([]);
  const [activePost, setActivePost] = useState(null);
  const [postGenLoading, setPostGenLoading] = useState(false);
  const [postTransforming, setPostTransforming] = useState(false);
  const [postRewriteFeedback, setPostRewriteFeedback] = useState('');
  const [postHumanizeLevel, setPostHumanizeLevel] = useState(2);
  const [postFilters, setPostFilters] = useState({ platform: '', post_type: '', status: '', profile_id: '', q: '' });
  const [convertPlatform, setConvertPlatform] = useState('facebook');
  const [convertToPostPlatform, setConvertToPostPlatform] = useState('facebook');
  const [convertAllProgress, setConvertAllProgress] = useState(null);
  const [convertAllResults, setConvertAllResults] = useState(null);
  const [convertLength, setConvertLength] = useState('medium');

  // ===== Round 5B: Carousels / Threads / Engagement / Community / Voice =====
  const [carouselForm, setCarouselForm] = useState({ platform: 'instagram', profile_id: '', topic: '', carousel_type: 'educational', slide_count: 7, tones: [], extra_context: '' });
  const [carousels, setCarousels] = useState([]);
  const [activeCarousel, setActiveCarousel] = useState(null);
  const [carouselLoading, setCarouselLoading] = useState(false);

  const [threadForm, setThreadForm] = useState({ platform: 'x_twitter', profile_id: '', topic: '', thread_type: 'educational', length: 7, tones: [], extra_context: '' });
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  const [batchForm, setBatchForm] = useState({ platform: 'facebook', profile_id: '', topic: '', count: 6, tones: [], extra_context: '' });
  const [batchDrafts, setBatchDrafts] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchMode, setBatchMode] = useState('engagement'); // engagement | community

  const [voiceForm, setVoiceForm] = useState({ posts_text: '', notes: '' });
  const [voiceResult, setVoiceResult] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);

  // ===== Brand Profile editor =====
  const emptyProfile = { name: '', niche: '', ideal_viewer: '', unique_angle: '', core_message: '', style: 'Educational, conversational', tone: 'Confident, warm, direct' };
  const [profileForm, setProfileForm] = useState(emptyProfile);
  const [editingProfileId, setEditingProfileId] = useState(null);

  // ----- Voice input on topic -----
  const voice = useVoiceInput((t) => setExtraContext(t));

  // ----- Fetch on mount -----
  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get('/api/admin/social-studio/brand-profiles'),
          api.get('/api/admin/social-studio/scripts?limit=100'),
        ]);
        setProfiles(p.data.profiles || []);
        setScripts(s.data.scripts || []);
      } catch (e) {
        toast.error('Failed to load Social Studio data');
      }
    })();
  }, []);

  const refreshScripts = async () => {
    const s = await api.get('/api/admin/social-studio/scripts?limit=100');
    setScripts(s.data.scripts || []);
  };

  const refreshProfiles = async () => {
    const p = await api.get('/api/admin/social-studio/brand-profiles');
    setProfiles(p.data.profiles || []);
  };

  // ===== Handlers =====
  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic for the video');
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        platform,
        profile_id: profileId || undefined,
        topic: topic.trim(),
        goal,
        extra_context: extraContext.trim(),
        variants,
        tones: selectedTones.length ? selectedTones : undefined,
        content_pillar: contentPillar || undefined,
      };
      if (formatCategory) {
        payload.format_category = formatCategory;
        payload.format_subtype = formatSubtype || undefined;
      }
      if (formatFaceless) payload.faceless = true;
      if (formatTextOnScreen) payload.text_on_screen = true;
      if (!profileId) {
        payload.niche = profileForm.niche || 'general entrepreneurship';
        payload.audience = profileForm.ideal_viewer || 'engaged adults online';
      }
      const res = await api.post('/api/admin/social-studio/generate-script', payload);
      const sc = res.data.script;
      setActiveScript(sc);
      setScripts((prev) => [sc, ...prev]);
      setTab('refine');
      toast.success(`Script generated for ${platform}`);
      // Auto-generate per-platform captions + recommended hashtags in the background (lean call).
      (async () => {
        try {
          const cr = await api.post(`/api/admin/social-studio/scripts/${sc.script_id}/platform-captions`, {});
          setActiveScript((cur) => {
            if (!cur || cur.script_id !== sc.script_id) return cur;
            const vs = [...(cur.variants || [])];
            const i = cur.selected_variant_idx || 0;
            if (vs[i]) vs[i] = { ...vs[i], platform_captions: cr.data.platform_captions };
            return { ...cur, variants: vs };
          });
        } catch (_e) { /* budget/timeout — user can still click "Generate per-platform captions" */ }
      })();
    } catch (e) {
      toast.error('Generation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  // ===== Phase 2 + 15: transformation buttons =====
  const isPending = (key) => !!pending[key];
  const anyPending = Object.keys(pending).length > 0;
  const setPendingKey = (key, val) => setPending((p) => {
    const next = { ...p };
    if (val) next[key] = true; else delete next[key];
    return next;
  });
  const handleTransform = async (endpoint, label, body = {}) => {
    if (!activeScript || pending[endpoint]) return;
    setPendingKey(endpoint, true);
    try {
      await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/${endpoint}`, body);
      const detail = await api.get(`/api/admin/social-studio/scripts/${activeScript.script_id}`);
      setActiveScript(detail.data.script);
      refreshScripts();
      toast.success(`${label} variant added`);
    } catch (e) {
      toast.error(`${label} failed: ` + (e.response?.data?.detail || e.message));
    } finally {
      setPendingKey(endpoint, false);
    }
  };

  const handleHumanize = (level) => handleTransform('humanize', `Humanize L${level}`, { script_id: activeScript?.script_id, level });
  const handleMakeCompliant = () => handleTransform('make-compliant', 'Make Compliant');
  const handleMakeMoreViral = () => handleTransform('make-more-viral', 'Make More Viral');
  const handleImproveLowScores = () => handleTransform('improve-low-scores', 'Improve Low Scores');
  const handleRewriteHook = (style) => handleTransform('rewrite-hook', `Rewrite Hook (${style})`, { style });
  const handleRewriteCta = (intent) => handleTransform('rewrite-cta', `Rewrite CTA (${intent})`, { intent });
  const handleMakeExtended = () => handleTransform('make-extended', 'Extended (2.5-3 min)');
  const handleRemoveFlags = () => handleTransform('remove-flagged-phrases', 'Flags Removed');

  const handlePlatformCaptions = async () => {
    if (!activeScript || pending['platform-captions']) return;
    setPendingKey('platform-captions', true);
    try {
      await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/platform-captions`, {});
      const detail = await api.get(`/api/admin/social-studio/scripts/${activeScript.script_id}`);
      setActiveScript(detail.data.script);
      toast.success('Per-platform captions ready');
    } catch (e) {
      toast.error('Captions failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPendingKey('platform-captions', false);
    }
  };

  // ===== Phase 7: Learn From This Script =====
  const handleLearnFromScript = async (direction) => {
    if (!activeScript || !activeScript.profile_id) {
      toast.error('Pick a script that has a brand profile attached');
      return;
    }
    try {
      await api.post(`/api/admin/social-studio/brand-profiles/${activeScript.profile_id}/learn-from-script`, {
        script_id: activeScript.script_id,
        direction,
      });
      refreshProfiles();
      toast.success('Brand profile updated');
    } catch (e) {
      toast.error('Learn failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Selected profile's content pillars (for the picker)
  const selectedProfile = useMemo(
    () => profiles.find((p) => p.profile_id === profileId),
    [profiles, profileId],
  );
  const availablePillars = selectedProfile?.content_pillars || [];

  // ===== Share link =====
  const handleCreateShare = async () => {
    if (!activeScript) return;
    try {
      const r = await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/share`);
      const fullUrl = `${window.location.origin}${r.data.share_url}`;
      setShareUrl(fullUrl);
      navigator.clipboard.writeText(fullUrl).catch(() => {});
      toast.success('Share link created and copied');
    } catch (e) {
      toast.error('Share failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSmartFill = async () => {
    if (!rawIdea || rawIdea.trim().length < 6) {
      toast.error('Type a sentence or two describing your idea');
      return;
    }
    setSmartFilling(true);
    try {
      const r = await api.post('/api/admin/social-studio/expand-idea', {
        idea: rawIdea.trim(),
        profile_id: profileId || undefined,
        platform,
      });
      const e = r.data.expanded || {};
      if (e.topic) setTopic(e.topic);
      if (e.goal) setGoal(e.goal);
      if (e.extra_context) setExtraContext(e.extra_context);
      if (Array.isArray(e.tones) && e.tones.length) {
        setSelectedTones(e.tones.filter((t) => TONES.includes(t)));
      }
      if (e.content_pillar && availablePillars.includes(e.content_pillar)) {
        setContentPillar(e.content_pillar);
      }
      setSmartFillResult(e);
      toast.success('Fields filled - review and Generate');
    } catch (err) {
      toast.error('Smart fill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSmartFilling(false);
    }
  };
  const handleRevokeShare = async () => {
    if (!activeScript) return;
    try {
      await api.delete(`/api/admin/social-studio/scripts/${activeScript.script_id}/share`);
      setShareUrl('');
      toast.success('Share link revoked');
    } catch (e) {
      toast.error('Revoke failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Pre-fill shareUrl from existing share_token on activeScript
  useEffect(() => {
    if (activeScript?.share_enabled && activeScript?.share_token) {
      setShareUrl(`${window.location.origin}/share/script/${activeScript.share_token}`);
    } else {
      setShareUrl('');
    }
  }, [activeScript]);

  // ===== Hook Engine =====
  useEffect(() => {
    if (tab !== 'hook_engine' || hookEngineCategories.length) return;
    api.get('/api/admin/social-studio/hook-engine/categories').then(
      (r) => setHookEngineCategories(r.data.categories || []),
      () => {},
    );
  }, [tab, hookEngineCategories.length]);

  // Load Proven Viral Templates taxonomy once (used in the Video generator).
  useEffect(() => {
    api.get('/api/admin/social-studio/viral-formats')
      .then((r) => setViralFormats(r.data.categories || []))
      .catch(() => {});
  }, []);

  const apiSuggestFormat = async ({ niche, audience, topic: t }) => {
    const r = await api.post('/api/admin/social-studio/viral-formats/suggest', {
      niche: niche || '', audience: audience || '', topic: t || '',
    });
    return r.data.suggestion || null;
  };

  const handleHookEngineGenerate = async () => {
    if (!hookEngineForm.niche || !hookEngineForm.audience || !hookEngineForm.topic) {
      toast.error('Niche, audience and topic are required');
      return;
    }
    setHookEngineLoading(true);
    try {
      const payload = {
        ...hookEngineForm,
        categories: hookEngineSelected.length ? hookEngineSelected : undefined,
        format_category: heFormat.category || undefined,
        format_subtype: heFormat.subtype || undefined,
      };
      if (heFormat.faceless) payload.faceless = true;
      if (heFormat.textOnScreen) payload.text_on_screen = true;
      const r = await api.post('/api/admin/social-studio/hook-engine/generate', payload);
      setHookEngineResults(r.data.hooks || []);
      toast.success(`${(r.data.hooks || []).length} hooks generated`);
    } catch (e) {
      toast.error('Hook engine failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setHookEngineLoading(false);
    }
  };

  const handleScoreSingleHook = async () => {
    if (!hookScoreText.trim()) {
      toast.error('Type a hook to score');
      return;
    }
    setHookScoring(true);
    try {
      const r = await api.post('/api/admin/social-studio/hook-engine/score', {
        text: hookScoreText.trim(),
        platform: hookEngineForm.platform,
        niche: hookEngineForm.niche,
        audience: hookEngineForm.audience,
      });
      setHookScoreResult(r.data.score);
    } catch (e) {
      toast.error('Score failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setHookScoring(false);
    }
  };

  // ===== Planner =====
  const refreshPlans = async (profile_id) => {
    const r = await api.get('/api/admin/social-studio/content-plans', { params: profile_id ? { profile_id } : {} });
    setPlans(r.data.plans || []);
  };
  useEffect(() => {
    if (tab === 'planner') refreshPlans();
  }, [tab]);

  const handleCreatePlan = async () => {
    if (!planForm.profile_id) {
      toast.error('Pick a brand profile');
      return;
    }
    setPlanLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/content-plans', planForm);
      setActivePlan(r.data.plan);
      refreshPlans();
      toast.success('Content plan created');
    } catch (e) {
      toast.error('Plan failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPlanLoading(false);
    }
  };

  const handleUpdatePlanItem = async (planId, itemId, patch) => {
    try {
      const r = await api.patch(`/api/admin/social-studio/content-plans/${planId}/items/${itemId}`, patch);
      if (activePlan?.plan_id === planId) setActivePlan({ ...activePlan, items: r.data.items });
      refreshPlans();
    } catch (e) {
      toast.error('Update failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this plan?')) return;
    await api.delete(`/api/admin/social-studio/content-plans/${planId}`);
    if (activePlan?.plan_id === planId) setActivePlan(null);
    refreshPlans();
    toast.success('Plan deleted');
  };

  const handleScriptFromPlanItem = async (planId, itemId) => {
    setPlanLoading(true);
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${planId}/items/${itemId}/generate-script`);
      setActiveScript(r.data.script);
      setScripts((prev) => [r.data.script, ...prev]);
      // Refresh plan to show script_id link
      const detail = await api.get(`/api/admin/social-studio/content-plans/${planId}`);
      setActivePlan(detail.data.plan);
      refreshPlans();
      toast.success('Script generated - switching to Refine');
      setTab('refine');
    } catch (e) {
      toast.error('Generate failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPlanLoading(false);
    }
  };

  // ===== Repurpose =====
  const handleRepurpose = async () => {
    if (!repurposeForm.source_text || repurposeForm.source_text.length < 80) {
      toast.error('Paste at least 80 characters of source content');
      return;
    }
    setRepurposeLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/repurpose', repurposeForm);
      setRepurposeClips(r.data.clips || []);
      toast.success(`${(r.data.clips || []).length} clips ready`);
    } catch (e) {
      toast.error('Repurpose failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRepurposeLoading(false);
    }
  };

  const handleUseRepurposeClip = (clip) => {
    setTopic(clip.title || clip.hook || 'Untitled');
    setExtraContext(`Repurposed clip:\n\nHook: ${clip.hook}\nPromise: ${clip.promise}\n\n${clip.distilled_point || ''}`);
    setPlatform(clip.suggested_platform || 'tiktok');
    setTab('generator');
    toast.success('Loaded into Generator');
  };

  // ===== Round 3: Trends =====
  const refreshTrends = async () => {
    const r = await api.get('/api/admin/social-studio/trends');
    setTrends(r.data.trends || []);
  };
  useEffect(() => { if (tab === 'trends') refreshTrends(); }, [tab]);

  const handleDiscoverTrends = async () => {
    if (!trendsForm.niche) { toast.error('Niche is required'); return; }
    setTrendsLoading(true);
    try {
      const payload = {
        niche: trendsForm.niche,
        audience: trendsForm.audience || '',
        focus: trendsForm.focus || '',
        competitor_handles: trendsForm.competitor_handles
          ? trendsForm.competitor_handles.split(/[,\s\n]+/).map((s) => s.trim()).filter(Boolean)
          : [],
        manual_paste: trendsForm.manual_paste || '',
      };
      const r = await api.post('/api/admin/social-studio/trends/discover', payload);
      setActiveTrend(r.data.trend);
      refreshTrends();
      toast.success('Trend report ready');
    } catch (e) {
      toast.error('Trends failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTrendsLoading(false);
    }
  };

  const handleDeleteTrend = async (id) => {
    if (!window.confirm('Delete this trend report?')) return;
    await api.delete(`/api/admin/social-studio/trends/${id}`);
    if (activeTrend?.trend_id === id) setActiveTrend(null);
    refreshTrends();
    toast.success('Deleted');
  };

  // ===== Round 3: Creator Analysis =====
  const refreshAnalyses = async () => {
    const r = await api.get('/api/admin/social-studio/creator-analysis');
    setAnalyses(r.data.analyses || []);
  };
  useEffect(() => { if (tab === 'creator') refreshAnalyses(); }, [tab]);

  const handleAnalyseCreator = async () => {
    if (!creatorForm.competitor_content || creatorForm.competitor_content.length < 80) {
      toast.error('Paste at least 80 characters of competitor content');
      return;
    }
    setCreatorLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/creator-analysis', creatorForm);
      setActiveAnalysis(r.data.analysis);
      refreshAnalyses();
      toast.success('Creator analysis ready');
    } catch (e) {
      toast.error('Analyse failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreatorLoading(false);
    }
  };

  const handleDeleteAnalysis = async (id) => {
    if (!window.confirm('Delete this analysis?')) return;
    await api.delete(`/api/admin/social-studio/creator-analysis/${id}`);
    if (activeAnalysis?.analysis_id === id) setActiveAnalysis(null);
    refreshAnalyses();
    toast.success('Deleted');
  };

  const handleUseRewrite = (rw) => {
    setTopic(rw.title || rw.hook);
    setExtraContext(`Hook: ${rw.hook}\n\nProblem: ${rw.problem}\n\nSolution: ${rw.solution}\n\nCTA: ${rw.cta}`);
    setTab('generator');
    toast.success('Loaded into Generator');
  };

  // ===== Round 3: Performance =====
  const refreshPerf = async (scriptId) => {
    if (!scriptId) return;
    const r = await api.get(`/api/admin/social-studio/scripts/${scriptId}/performance`);
    setPerfMetrics(r.data.metrics || []);
    setPerfTotals(r.data.totals || { views: 0, engagement: 0, avg_engagement_rate: 0 });
  };
  const refreshPerfSummary = async () => {
    const r = await api.get('/api/admin/social-studio/performance/summary?limit=10');
    setPerfSummary(r.data.top_performers || []);
  };
  useEffect(() => {
    if (tab === 'performance') {
      refreshPerfSummary();
      if (activeScript) refreshPerf(activeScript.script_id);
    }
  }, [tab, activeScript]);

  const handleAddPerf = async () => {
    if (!activeScript) { toast.error('Pick a script from the library first'); return; }
    setPerfLoading(true);
    try {
      await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/performance`, perfForm);
      setPerfForm({ views: 0, likes: 0, comments: 0, shares: 0, saves: 0, follows_gained: 0, link_clicks: 0, posted_at: '', notes: '' });
      refreshPerf(activeScript.script_id);
      refreshPerfSummary();
      toast.success('Metric saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPerfLoading(false);
    }
  };

  const handleDeletePerf = async (metricId) => {
    await api.delete(`/api/admin/social-studio/performance/${metricId}`);
    refreshPerf(activeScript?.script_id);
    refreshPerfSummary();
    toast.success('Deleted');
  };

  // ===== Library: filtered fetch & CSV exports =====
  const handleApplyLibFilters = async () => {
    const params = {};
    Object.entries(libFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get('/api/admin/social-studio/scripts-filtered', { params });
    setScripts(r.data.scripts || []);
    toast.success(`${r.data.count} script(s) match`);
  };

  const handleExportLibrary = () => {
    const params = new URLSearchParams();
    Object.entries(libFilters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const url = `${API_URL}/api/admin/social-studio/library/export.csv${params.toString() ? '?' + params.toString() : ''}`;
    window.open(url, '_blank');
  };

  const handleExportScript = (scriptId) => {
    window.open(`${API_URL}/api/admin/social-studio/scripts/${scriptId}/export.csv`, '_blank');
  };

  const handleExportPlan = (planId) => {
    window.open(`${API_URL}/api/admin/social-studio/content-plans/${planId}/export.csv`, '_blank');
  };

  // ===== Round 4: tags =====
  const handleAddTag = async (raw) => {
    if (!activeScript || !raw) return;
    const tag = raw.trim().toLowerCase();
    if (!tag) return;
    try {
      const r = await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/tags`, { tag });
      setActiveScript({ ...activeScript, tags: r.data.tags });
      refreshScripts();
      toast.success('Tag added');
    } catch (e) {
      toast.error('Add tag failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const handleRemoveTag = async (tag) => {
    if (!activeScript) return;
    try {
      const r = await api.delete(`/api/admin/social-studio/scripts/${activeScript.script_id}/tags/${encodeURIComponent(tag)}`);
      setActiveScript({ ...activeScript, tags: r.data.tags });
      refreshScripts();
    } catch (e) {
      toast.error('Remove failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ===== Round 4: AI Insights =====
  const refreshInsights = async () => {
    const r = await api.get('/api/admin/social-studio/performance/insights');
    setInsights(r.data.insights || []);
  };
  useEffect(() => { if (tab === 'performance') refreshInsights(); }, [tab]);

  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/performance/insights', { top_n: 5, bottom_n: 5, focus: insightFocus || '' });
      setActiveInsight(r.data.insight);
      refreshInsights();
      toast.success('Insights ready');
    } catch (e) {
      toast.error('Insights failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setInsightsLoading(false);
    }
  };

  // ===== Round 5A: Post Studio handlers =====
  const refreshPosts = async (filters = postFilters) => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get('/api/admin/social-studio/posts', { params });
    setPosts(r.data.posts || []);
  };
  useEffect(() => {
    if (tab === 'post_studio' && postMeta.platforms.length === 0) {
      api.get('/api/admin/social-studio/posts/meta').then((r) => setPostMeta(r.data)).catch(() => {});
    }
    if (tab === 'post_studio') refreshPosts();
  }, [tab]);

  const handleGeneratePost = async () => {
    if (!postForm.topic) { toast.error('Topic is required'); return; }
    setPostGenLoading(true);
    try {
      const payload = { ...postForm };
      if (!payload.framework) delete payload.framework;
      if (!payload.emoji_level) delete payload.emoji_level;
      if (!payload.content_pillar) delete payload.content_pillar;
      const r = await api.post('/api/admin/social-studio/posts/generate', payload);
      setActivePost(r.data.post);
      setPostSubTab('refine');
      refreshPosts();
      toast.success('Post generated');
      // Auto-generate recommended hashtags per platform in the background (lean call).
      const newPost = r.data.post;
      (async () => {
        try {
          const hr = await api.post(`/api/admin/social-studio/posts/${newPost.post_id}/platform-hashtags`, {});
          setActivePost((cur) => {
            if (!cur || cur.post_id !== newPost.post_id) return cur;
            const vs = [...(cur.variants || [])];
            const i = cur.selected_variant_idx || 0;
            if (vs[i]) vs[i] = { ...vs[i], platform_hashtags: hr.data.platform_hashtags };
            return { ...cur, variants: vs };
          });
        } catch (_e) { /* budget/timeout — user can click the manual button */ }
      })();
    } catch (e) {
      toast.error('Generate failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPostGenLoading(false);
    }
  };

  const handlePostPlatformHashtags = async () => {
    if (!activePost || pending['post-platform-hashtags']) return;
    setPendingKey('post-platform-hashtags', true);
    try {
      const hr = await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/platform-hashtags`, {});
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      toast.success('Per-platform hashtags ready');
    } catch (e) {
      toast.error('Hashtags failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPendingKey('post-platform-hashtags', false);
    }
  };

  const callPostTransform = async (path, body) => {
    if (!activePost) return;
    setPostTransforming(true);
    try {
      const r = await api.post(`/api/admin/social-studio/posts/${activePost.post_id}${path}`, body || {});
      // Refresh the active post
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      refreshPosts();
      toast.success(`${r.data.version_label} variant added`);
    } catch (e) {
      toast.error('Transform failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPostTransforming(false);
    }
  };

  const handlePostHumanize = () => callPostTransform('/humanize', { level: postHumanizeLevel });
  const handlePostMoreViral = () => callPostTransform('/make-more-viral');
  const handlePostMoreEmotional = () => callPostTransform('/make-more-emotional');
  const handlePostShorter = () => callPostTransform('/make-shorter');
  const handlePostLonger = () => callPostTransform('/make-longer');
  const handlePostCompliant = () => callPostTransform('/make-compliant');
  const handlePostRewrite = () => {
    if (!postRewriteFeedback.trim()) { toast.error('Type rewrite feedback first'); return; }
    callPostTransform('/rewrite', { feedback: postRewriteFeedback.trim() });
    setPostRewriteFeedback('');
  };

  const handleSelectPostVariant = async (idx) => {
    if (!activePost) return;
    await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/select-variant`, null, { params: { variant_idx: idx } });
    const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
    setActivePost(detail.data.post);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    await api.delete(`/api/admin/social-studio/posts/${postId}`);
    if (activePost?.post_id === postId) setActivePost(null);
    refreshPosts();
    toast.success('Deleted');
  };

  const handlePostStatusChange = async (postId, status) => {
    await api.patch(`/api/admin/social-studio/posts/${postId}`, { status });
    refreshPosts();
    if (activePost?.post_id === postId) {
      const detail = await api.get(`/api/admin/social-studio/posts/${postId}`);
      setActivePost(detail.data.post);
    }
    toast.success(`Marked ${status}`);
  };

  // ===== Round 5C: Image gen / scheduling / convert-to-video / compliance recheck =====
  const [imageStyle, setImageStyle] = useState('photorealistic');
  const [imageAspect, setImageAspect] = useState('square');
  const [imageCustomPrompt, setImageCustomPrompt] = useState('');
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageSuggestions, setImageSuggestions] = useState([]);
  const [imageSuggestionsLoading, setImageSuggestionsLoading] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleChannel, setScheduleChannel] = useState('');
  const [scheduleAutoPublish, setScheduleAutoPublish] = useState(false);
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  const [convertTarget, setConvertTarget] = useState('instagram_reels');
  const [convertGoal, setConvertGoal] = useState('drive saves and comments');
  const [convertLoading, setConvertLoading] = useState(false);
  const [complianceLoading, setComplianceLoading] = useState(false);

  // Scheduler sub-tab state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerProfiles, setSchedulerProfiles] = useState([]);
  const [schedulerQueue, setSchedulerQueue] = useState([]);
  const [bufferTokenInput, setBufferTokenInput] = useState('');

  // One-click Pipeline state
  const [pipelineIdea, setPipelineIdea] = useState('');
  const [pipelineGenerateImage, setPipelineGenerateImage] = useState(true);
  const [pipelineSchedule, setPipelineSchedule] = useState(false);
  const [pipelineDate, setPipelineDate] = useState('');
  const [pipelineTime, setPipelineTime] = useState('09:00');
  const [pipelineLoading, setPipelineLoading] = useState(false);

  // Round 5D: native targets + recurring templates
  const [nativeTargets, setNativeTargets] = useState([]);
  const [nativeForm, setNativeForm] = useState({ provider: 'facebook', label: '', access_token: '', account_id: '' });
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [recurringForm, setRecurringForm] = useState({
    name: '', cadence: 'weekly', weekdays: [1], time_of_day: '09:00',
    mode: 'generator', platform: 'facebook', profile_id: '', post_type: 'storytelling',
    topic: '', tones: [], length: 'medium', extra_context: '', target_id: '',
  });

  // Round 5E: best-times intel + hosted image url
  const [bestTimes, setBestTimes] = useState(null);
  const [hostedImageUrl, setHostedImageUrl] = useState('');

  // Round 5F: Build-Next-Week + Remix
  const [buildWeekDays, setBuildWeekDays] = useState(7);
  const [buildWeekJobId, setBuildWeekJobId] = useState('');
  const [buildWeekJob, setBuildWeekJob] = useState(null);
  const [buildWeekLoading, setBuildWeekLoading] = useState(false);
  const [remixSource, setRemixSource] = useState('');
  const [remixPlatform, setRemixPlatform] = useState('facebook');
  const [remixTone, setRemixTone] = useState('');
  const [remixAngle, setRemixAngle] = useState('');
  const [remixVariants, setRemixVariants] = useState([]);
  const [remixLoading, setRemixLoading] = useState(false);

  // Round 5G: Video Remix + URL ingest
  const [vremixUrl, setVremixUrl] = useState('');
  const [vremixSource, setVremixSource] = useState('');
  const [vremixPlatform, setVremixPlatform] = useState('instagram_reels');
  const [vremixTone, setVremixTone] = useState('');
  const [vremixAngle, setVremixAngle] = useState('');
  const [vremixIngestMeta, setVremixIngestMeta] = useState(null);
  const [vremixVariants, setVremixVariants] = useState([]);
  const [vremixLoading, setVremixLoading] = useState(false);

  const handleVideoIngest = async (forUse = 'remix') => {
    if (!vremixUrl.trim()) { toast.error('Paste a video URL'); return; }
    setVremixLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/video-ingest', { url: vremixUrl.trim(), purpose: forUse });
      setVremixIngestMeta(r.data);
      setVremixSource(r.data.transcript || '');
      toast.success(`Ingested: ${r.data.title || r.data.platform || 'video'} (${r.data.transcript_length || 0} chars)`);
    } catch (e) {
      toast.error('Ingest failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVremixLoading(false);
    }
  };

  const handleVideoRemix = async () => {
    if (vremixSource.trim().length < 40 && !vremixUrl.trim()) { toast.error('Paste source text or a video URL'); return; }
    setVremixLoading(true);
    try {
      const payload = {
        target_platform: vremixPlatform,
        profile_id: profileId || null,
        target_tone: vremixTone || null,
        angle_shift: vremixAngle || null,
        save_as_script: true,
      };
      if (vremixSource.trim()) payload.source_text = vremixSource.trim();
      else payload.source_url = vremixUrl.trim();
      const r = await api.post('/api/admin/social-studio/scripts/remix', payload);
      setVremixVariants(r.data.variants || []);
      toast.success(`${(r.data.variants || []).length} remix variant(s) ${r.data.saved_script_id ? '· saved' : ''}`);
      refreshScripts();
    } catch (e) {
      toast.error('Remix failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVremixLoading(false);
    }
  };

  // Carry-over: copy current data into the OTHER studio
  const handleCarryOver = (payload) => {
    // payload: { type: 'hook' | 'idea' | 'topic' | 'plan' | 'brand', text?, items? }
    const t = payload.type || 'topic';
    const text = payload.text || (payload.items || []).join('\n');
    if (studioMode === 'video') {
      // Carry into Post Studio Generator topic
      setStudioMode('post');
      setTab('post_studio');
      setPostSubTab('generator');
      setPostForm((f) => ({ ...f, topic: (text || '').slice(0, 240), extra_context: text }));
      toast.success(`Carried over to Post Studio - opened Post Generator`);
    } else {
      setStudioMode('video');
      setTab('generator');
      setTopic((text || '').slice(0, 240));
      setExtraContext(text);
      toast.success(`Carried over to Video Studio - opened Script Generator`);
    }
  };

  // Content Intelligence → land directly in Post or Video Studio with the source pre-loaded
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') !== 'intel') return;
    // Batch: a whole folder of clips sent from Content Intelligence → one grounded brief.
    const rawBatch = localStorage.getItem('dca_intel_seed_batch');
    if (params.get('batch') === '1' && rawBatch) {
      try {
        const b = JSON.parse(rawBatch);
        const srcs = (b.sources || []).slice(0, 12);
        const list = srcs.map((s, i) => `${i + 1}. "${s.title}"${s.publisher ? ' — ' + s.publisher : ''}${s.url ? ' (' + s.url + ')' : ''}`).join('\n');
        const ctx = `Create content grounded in these ${srcs.length} credible sources from my "${b.label}" research folder:\n${list}\nSynthesize the common themes across them. Never invent facts these sources do not state.`;
        if (params.get('mode') === 'video') {
          setStudioMode('video'); setTab('generator');
          setTopic((b.label || 'Research roundup').slice(0, 200)); setExtraContext(ctx);
          toast.info(`Loaded ${srcs.length} sources from your "${b.label}" folder into Video Studio — review and Generate.`);
        } else {
          setStudioMode('post'); setTab('post_studio'); setPostSubTab('generator');
          setPostForm((f) => ({ ...f, topic: (b.label || 'Research roundup').slice(0, 200), extra_context: ctx }));
          toast.info(`Loaded ${srcs.length} sources from your "${b.label}" folder into Post Studio — review and Generate.`);
        }
      } catch (e) { /* ignore */ }
      localStorage.removeItem('dca_intel_seed_batch');
      return;
    }
    const raw = localStorage.getItem('dca_intel_seed');
    if (!raw) return;
    try {
      const s = JSON.parse(raw);
      const ctx = `Base this on this credible source: "${s.title}". ${(s.summary || '').slice(0, 400)} (Source: ${s.publisher || ''}${s.url ? ' — ' + s.url : ''}). Reference it with language like "Based on the linked source...". Never invent facts the source does not state.`;
      if (params.get('mode') === 'video') {
        setStudioMode('video');
        setTab('generator');
        setTopic((s.title || '').slice(0, 200));
        setExtraContext(ctx);
        toast.info('Loaded source from Content Intelligence into Video Studio — review and Generate.');
      } else {
        setStudioMode('post');
        setTab('post_studio');
        setPostSubTab('generator');
        setPostForm((f) => ({ ...f, topic: (s.title || '').slice(0, 200), extra_context: ctx }));
        toast.info('Loaded source from Content Intelligence into Post Studio — review and Generate.');
      }
    } catch (e) { /* ignore */ }
    localStorage.removeItem('dca_intel_seed');
  }, []);

  // Land in a specific studio when arriving from the Content Suite nav (?mode=post|video)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get('mode');
    if (m === 'post') { setStudioMode('post'); setTab('post_studio'); setPostSubTab('generator'); }
    else if (m === 'video') { setStudioMode('video'); setTab('generator'); }
  }, []);

  // Show a dismissible upgrade nudge when a daily AI limit is hit anywhere in the studio
  useEffect(() => {
    const onHit = (e) => setLimitNudge(e.detail || {});
    window.addEventListener('studio-limit-hit', onHit);
    return () => window.removeEventListener('studio-limit-hit', onHit);
  }, []);

  const handleBuildNextWeek = async () => {
    setBuildWeekLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/posts/build-next-week', {
        profile_id: postForm.profile_id || null,
        platforms: ['facebook', 'instagram', 'linkedin', 'x_twitter'],
        days: buildWeekDays,
        schedule: true,
        auto_publish: false,
        image_for_each: false,
      });
      setBuildWeekJobId(r.data.job_id);
      toast.success(`Building ${buildWeekDays} days in the background...`);
      // Poll every 8s
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/api/admin/social-studio/posts/build-next-week/${r.data.job_id}`);
          setBuildWeekJob(res.data);
          if (res.data.status === 'complete' || res.data.status === 'failed') {
            clearInterval(poll);
            setBuildWeekLoading(false);
            refreshPosts();
            refreshSchedulerQueue();
            toast.success(`Build complete: ${res.data.plan?.length || 0} posts created`);
          }
        } catch (_e) { clearInterval(poll); setBuildWeekLoading(false); }
      }, 8000);
    } catch (e) {
      toast.error('Build failed: ' + (e.response?.data?.detail || e.message));
      setBuildWeekLoading(false);
    }
  };

  const handleRemix = async () => {
    if (remixSource.trim().length < 40) { toast.error('Paste at least 40 chars'); return; }
    setRemixLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/posts/remix', {
        source_text: remixSource,
        target_platform: remixPlatform,
        profile_id: postForm.profile_id || null,
        target_tone: remixTone || null,
        angle_shift: remixAngle || null,
        variants: 3,
        save_as_post: true,
      });
      setRemixVariants(r.data.variants || []);
      toast.success(`${(r.data.variants || []).length} remixes ready ${r.data.saved_post_id ? '· top saved' : ''}`);
      refreshPosts();
    } catch (e) {
      toast.error('Remix failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRemixLoading(false);
    }
  };

  const handleConvertScriptToPost = async (scriptId, targetPlatform = 'facebook', length = 'medium', autoSwitch = false) => {
    if (pending['convert-to-post']) return;
    setPendingKey('convert-to-post', true);
    try {
      const r = await api.post(`/api/admin/social-studio/scripts/${scriptId}/convert-to-post`, {
        target_platform: targetPlatform, length, tones: ['bold', 'warm'],
      });
      setActivePost(r.data.post);
      refreshPosts();
      if (autoSwitch) {
        setStudioMode('post');
        setTab('post_studio');
        setPostSubTab('refine');
        toast.success(`Post created: ${r.data.post_id}`);
      } else {
        // Non-jarring: stay on the video script; let the user choose when to switch.
        toast.success('Converted to a text post', {
          description: 'Saved to Post Studio → Refine. Your video script stays right here.',
          action: {
            label: 'View in Post Studio',
            onClick: () => { setStudioMode('post'); setTab('post_studio'); setPostSubTab('refine'); },
          },
        });
      }
    } catch (e) {
      toast.error('Convert failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPendingKey('convert-to-post', false);
    }
  };

  // Convert this video script into text posts for ALL major platforms in one go.
  // Fires sequential (independent, <60s each) calls to the verified convert endpoint
  // to stay well under the 60s ingress hard-timeout.
  const handleConvertToAllPlatforms = async () => {
    if (!activeScript || pending['convert-all']) return;
    const targets = [
      { key: 'facebook', label: 'Facebook' },
      { key: 'linkedin', label: 'LinkedIn' },
      { key: 'instagram', label: 'Instagram' },
      { key: 'x_twitter', label: 'X / Twitter' },
    ];
    setPendingKey('convert-all', true);
    let lastPost = null; let ok = 0; const failed = []; const results = [];
    for (let i = 0; i < targets.length; i++) {
      setConvertAllProgress({ done: i, total: targets.length, current: targets[i].label });
      try {
        const r = await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/convert-to-post`, {
          target_platform: targets[i].key, length: 'medium', tones: ['bold', 'warm'],
        });
        lastPost = r.data.post; ok += 1;
        results.push({ key: targets[i].key, label: targets[i].label, post: r.data.post });
      } catch (e) {
        failed.push(targets[i].label);
      }
    }
    setConvertAllProgress(null);
    setPendingKey('convert-all', false);
    if (lastPost) setActivePost(lastPost);
    refreshPosts();
    if (ok > 0) {
      setConvertAllResults(results);
      toast.success(`Created ${ok} platform post${ok === 1 ? '' : 's'}${failed.length ? ` · ${failed.join(', ')} failed` : ''}`, {
        description: 'Review & copy each version below, or open them in Post Studio → Library.',
      });
    } else {
      toast.error('Convert to all platforms failed' + (failed.length ? `: ${failed.join(', ')}` : ''));
    }
  };

  const refreshBestTimes = async (platform) => {
    try {
      const url = platform ? `/api/admin/social-studio/best-times?platform=${platform}` : '/api/admin/social-studio/best-times';
      const r = await api.get(url);
      setBestTimes(r.data.recommendations || {});
    } catch (e) {
      console.warn('refreshBestTimes failed:', e?.response?.data?.detail || e.message);
    }
  };

  const handleOAuthConnect = (provider) => {
    // Open OAuth start in a new tab; the callback page will show "Connected" + link back.
    const url = `${API_URL}/api/admin/social-studio/oauth/${provider}/start`;
    window.open(url, '_blank', 'width=620,height=720');
  };

  const handleHostImage = async (postId, imageId) => {
    try {
      const r = await api.post(`/api/admin/social-studio/posts/${postId}/images/${imageId}/host`);
      const url = r.data.public_url;
      setHostedImageUrl(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success('Public URL copied to clipboard');
    } catch (e) {
      toast.error('Host failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const refreshNativeTargets = async () => {
    try {
      const r = await api.get('/api/admin/social-studio/scheduler/native-targets');
      setNativeTargets(r.data.targets || []);
    } catch (e) {
      console.warn('refreshNativeTargets failed:', e?.response?.data?.detail || e.message);
    }
  };
  const handleSaveNativeTarget = async () => {
    if (!nativeForm.access_token.trim() || !nativeForm.label.trim()) { toast.error('Label + access token required'); return; }
    try {
      await api.post('/api/admin/social-studio/scheduler/native-targets', nativeForm);
      setNativeForm({ provider: 'facebook', label: '', access_token: '', account_id: '' });
      refreshNativeTargets();
      toast.success('Native target connected');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const handleDeleteNativeTarget = async (id) => {
    if (!window.confirm('Disconnect this account?')) return;
    await api.delete(`/api/admin/social-studio/scheduler/native-targets/${id}`);
    refreshNativeTargets();
    toast.success('Disconnected');
  };
  const handlePublishNow = async (postId, targetId) => {
    try {
      const r = await api.post(`/api/admin/social-studio/posts/${postId}/publish-now`, { target_id: targetId });
      if (r.data.result?.success) {
        toast.success(`Published! provider id ${r.data.result.provider_post_id || ''}`);
        if (activePost?.post_id === postId) {
          const detail = await api.get(`/api/admin/social-studio/posts/${postId}`);
          setActivePost(detail.data.post);
        }
      } else {
        toast.error('Publish failed: ' + (r.data.result?.error || 'unknown'));
      }
    } catch (e) {
      toast.error('Publish failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const refreshRecurring = async () => {
    try {
      const r = await api.get('/api/admin/social-studio/scheduler/recurring');
      setRecurringTemplates(r.data.templates || []);
    } catch (e) {
      console.warn('refreshRecurring failed:', e?.response?.data?.detail || e.message);
    }
  };
  const handleCreateRecurring = async () => {
    if (!recurringForm.name.trim() || !recurringForm.topic.trim()) { toast.error('Name + topic required'); return; }
    try {
      await api.post('/api/admin/social-studio/scheduler/recurring', recurringForm);
      setRecurringForm({ ...recurringForm, name: '', topic: '' });
      refreshRecurring();
      toast.success('Recurring template created');
    } catch (e) {
      toast.error('Create failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const handleRunRecurringNow = async (id) => {
    try {
      const r = await api.post(`/api/admin/social-studio/scheduler/recurring/${id}/run-now`);
      const arts = r.data.artifacts || {};
      const parts = [`Post ${arts.post_id || 'created'}`];
      if (arts.publish?.success) parts.push(`published`);
      else if (arts.publish?.error) parts.push(`publish failed`);
      toast.success(parts.join(' · '));
      refreshRecurring();
    } catch (e) {
      toast.error('Run failed: ' + (e.response?.data?.detail || e.message));
    }
  };
  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/api/admin/social-studio/scheduler/recurring/${id}`);
    refreshRecurring();
    toast.success('Deleted');
  };
  const togglePatchRecurring = async (id, active) => {
    await api.patch(`/api/admin/social-studio/scheduler/recurring/${id}`, { active });
    refreshRecurring();
  };

  const handleRunPipeline = async () => {
    if (!pipelineIdea.trim()) { toast.error('Type your idea first'); return; }
    setPipelineLoading(true);
    try {
      const payload = {
        idea: pipelineIdea.trim(),
        platform: postForm.platform,
        profile_id: postForm.profile_id || null,
        post_type: postForm.post_type,
        tones: postForm.tones,
        length: postForm.length,
        generate_image: pipelineGenerateImage,
        image_style: imageStyle,
        image_aspect: imageAspect,
        schedule: pipelineSchedule,
      };
      if (pipelineSchedule && pipelineDate) {
        payload.publish_at = new Date(`${pipelineDate}T${pipelineTime || '09:00'}:00`).toISOString();
      }
      const r = await api.post('/api/admin/social-studio/posts/pipeline', payload);
      setActivePost(r.data.post);
      setPostSubTab('refine');
      refreshPosts();
      const arts = r.data.artifacts || {};
      const parts = ['Post created'];
      if (arts.image) parts.push('image generated');
      else if (arts.image_error) parts.push('image skipped');
      if (arts.schedule) parts.push('scheduled');
      else if (arts.schedule_error) parts.push('schedule failed');
      toast.success(parts.join(' · '));
      setPipelineIdea('');
    } catch (e) {
      toast.error('Pipeline failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!activePost) return;
    setImageGenLoading(true);
    try {
      const body = { style: imageStyle, aspect_hint: imageAspect };
      if (imageCustomPrompt.trim()) body.prompt = imageCustomPrompt.trim();
      await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/generate-image`, body);
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      toast.success('Image generated');
    } catch (e) {
      toast.error('Image gen failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImageGenLoading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!activePost) return;
    try {
      await api.delete(`/api/admin/social-studio/posts/${activePost.post_id}/images/${imageId}`);
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      toast.success('Image removed');
    } catch (e) {
      toast.error('Delete image failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleSuggestImages = async () => {
    if (!activePost) return;
    setImageSuggestionsLoading(true);
    try {
      const r = await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/suggest-images`, { count: 3 });
      setImageSuggestions(r.data.suggestions || []);
      toast.success(`${(r.data.suggestions || []).length} alternate concepts`);
    } catch (e) {
      toast.error('Suggest failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImageSuggestionsLoading(false);
    }
  };

  const handleSchedulePost = async () => {
    if (!activePost) return;
    if (!scheduleDate) { toast.error('Pick a date'); return; }
    setSchedulingLoading(true);
    try {
      const iso = new Date(`${scheduleDate}T${scheduleTime || '09:00'}:00`).toISOString();
      await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/schedule`, {
        publish_at: iso,
        channel: scheduleChannel || null,
        auto_publish: scheduleAutoPublish,
      });
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      refreshPosts();
      toast.success(`Scheduled for ${new Date(iso).toLocaleString()}`);
    } catch (e) {
      toast.error('Schedule failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSchedulingLoading(false);
    }
  };

  const handleCancelSchedule = async (scheduleId) => {
    if (!activePost) return;
    try {
      await api.delete(`/api/admin/social-studio/posts/${activePost.post_id}/schedule/${scheduleId}`);
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      refreshPosts();
      toast.success('Schedule cancelled');
    } catch (e) {
      toast.error('Cancel failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleConvertToVideo = async () => {
    if (!activePost) return;
    setConvertLoading(true);
    try {
      const r = await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/convert-to-video`, {
        target_platform: convertTarget, goal: convertGoal,
      });
      toast.success(`Video script created: ${r.data.script_id}`);
      // Switch to the refine (video) tab
      setActiveScript(r.data.script);
      setTab('refine');
    } catch (e) {
      toast.error('Convert failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setConvertLoading(false);
    }
  };

  const handleComplianceRecheck = async () => {
    if (!activePost) return;
    setComplianceLoading(true);
    try {
      await api.post(`/api/admin/social-studio/posts/${activePost.post_id}/compliance-check`);
      const detail = await api.get(`/api/admin/social-studio/posts/${activePost.post_id}`);
      setActivePost(detail.data.post);
      toast.success('Compliance updated');
    } catch (e) {
      toast.error('Compliance failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setComplianceLoading(false);
    }
  };

  const refreshSchedulerStatus = async () => {
    try {
      const r = await api.get('/api/admin/social-studio/scheduler/status');
      setSchedulerStatus(r.data);
    } catch (e) {
      console.warn('refreshSchedulerStatus failed:', e?.response?.data?.detail || e.message);
    }
  };
  const refreshSchedulerProfiles = async () => {
    try {
      const r = await api.get('/api/admin/social-studio/scheduler/profiles');
      setSchedulerProfiles(r.data.profiles || []);
    } catch (e) {
      console.warn('refreshSchedulerProfiles failed:', e?.response?.data?.detail || e.message);
    }
  };
  const refreshSchedulerQueue = async () => {
    try {
      const r = await api.get('/api/admin/social-studio/scheduler/queue?limit=200');
      setSchedulerQueue(r.data.items || []);
    } catch (e) {
      console.warn('refreshSchedulerQueue failed:', e?.response?.data?.detail || e.message);
    }
  };
  const handleSaveBufferToken = async () => {
    try {
      await api.post('/api/admin/social-studio/scheduler/config', {
        buffer_access_token: bufferTokenInput,
      });
      setBufferTokenInput('');
      await refreshSchedulerStatus();
      await refreshSchedulerProfiles();
      toast.success('Scheduler config saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  // helper for post tone selector
  const togglePostTone = (t) => {
    setPostForm((prev) => ({
      ...prev,
      tones: prev.tones.includes(t) ? prev.tones.filter((x) => x !== t) : [...prev.tones, t],
    }));
  };

  // ===== Round 5B handlers =====
  const refreshCarousels = async () => {
    const r = await api.get('/api/admin/social-studio/carousels');
    setCarousels(r.data.carousels || []);
  };
  const refreshThreads = async () => {
    const r = await api.get('/api/admin/social-studio/threads');
    setThreads(r.data.threads || []);
  };
  useEffect(() => {
    if (tab === 'post_studio' && postSubTab === 'carousels') refreshCarousels();
    if (tab === 'post_studio' && postSubTab === 'threads') refreshThreads();
    if (tab === 'post_studio' && postSubTab === 'scheduler') {
      refreshSchedulerStatus();
      refreshSchedulerProfiles();
      refreshSchedulerQueue();
      refreshNativeTargets();
      refreshRecurring();
      refreshBestTimes();
    }
  }, [tab, postSubTab]);

  const handleGenerateCarousel = async () => {
    if (!carouselForm.topic) { toast.error('Topic is required'); return; }
    setCarouselLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/carousels/generate', carouselForm);
      setActiveCarousel(r.data.carousel);
      refreshCarousels();
      toast.success('Carousel ready');
    } catch (e) {
      toast.error('Carousel failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCarouselLoading(false);
    }
  };
  const handleDeleteCarousel = async (id) => {
    if (!window.confirm('Delete this carousel?')) return;
    await api.delete(`/api/admin/social-studio/carousels/${id}`);
    if (activeCarousel?.carousel_id === id) setActiveCarousel(null);
    refreshCarousels();
    toast.success('Deleted');
  };

  const handleGenerateThread = async () => {
    if (!threadForm.topic) { toast.error('Topic is required'); return; }
    setThreadLoading(true);
    try {
      const r = await api.post('/api/admin/social-studio/threads/generate', threadForm);
      setActiveThread(r.data.thread);
      refreshThreads();
      toast.success('Thread ready');
    } catch (e) {
      toast.error('Thread failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setThreadLoading(false);
    }
  };
  const handleDeleteThread = async (id) => {
    if (!window.confirm('Delete this thread?')) return;
    await api.delete(`/api/admin/social-studio/threads/${id}`);
    if (activeThread?.thread_id === id) setActiveThread(null);
    refreshThreads();
    toast.success('Deleted');
  };

  const handleGenerateBatch = async () => {
    if (!batchForm.topic) { toast.error('Topic is required'); return; }
    setBatchLoading(true);
    try {
      const r = await api.post(`/api/admin/social-studio/${batchMode}/generate`, batchForm);
      setBatchDrafts(r.data.drafts || []);
      toast.success(`${(r.data.drafts || []).length} drafts generated`);
    } catch (e) {
      toast.error('Batch failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBatchLoading(false);
    }
  };
  const handleSaveDraft = async (draft) => {
    try {
      await api.post('/api/admin/social-studio/drafts/save-as-post', {
        platform: batchForm.platform,
        profile_id: batchForm.profile_id || undefined,
        topic: batchForm.topic,
        post_type: batchMode,
        style: draft.style,
        draft,
      });
      refreshPosts();
      toast.success('Saved to Post Library');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleVoiceTrain = async () => {
    if (!editingProfileId) { toast.error('Open or create a brand profile first'); return; }
    const posts_list = voiceForm.posts_text.split(/\n\s*---+\s*\n|\n{3,}/).map((s) => s.trim()).filter((s) => s.length >= 20);
    if (posts_list.length < 3) { toast.error('Paste at least 3 real posts (separate with blank lines or "---")'); return; }
    setVoiceLoading(true);
    try {
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${editingProfileId}/train-from-posts`, {
        posts: posts_list, notes: voiceForm.notes, auto_apply: true,
      });
      setVoiceResult(r.data);
      // Refresh profile form with the new values
      const updated = r.data.profile;
      setProfileForm({
        name: updated.name, niche: updated.niche, ideal_viewer: updated.ideal_viewer,
        unique_angle: updated.unique_angle, core_message: updated.core_message,
        style: updated.style, tone: updated.tone,
        content_pillars: updated.content_pillars || [],
      });
      refreshProfiles();
      toast.success(`Voice trained from ${posts_list.length} posts`);
    } catch (e) {
      toast.error('Voice train failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setVoiceLoading(false);
    }
  };

  const handleRefine = async () => {
    if (!activeScript || !refineFeedback.trim()) return;
    setRefining(true);
    try {
      const res = await api.post('/api/admin/social-studio/refine-script', {
        script_id: activeScript.script_id,
        feedback: refineFeedback.trim(),
      });
      const detail = await api.get(`/api/admin/social-studio/scripts/${activeScript.script_id}`);
      setActiveScript(detail.data.script);
      setRefineFeedback('');
      toast.success('Variant added - switched to refined version');
      refreshScripts();
    } catch (e) {
      toast.error('Refine failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRefining(false);
    }
  };

  const handleSelectVariant = async (idx) => {
    if (!activeScript) return;
    try {
      await api.post(`/api/admin/social-studio/scripts/${activeScript.script_id}/select-variant?variant_idx=${idx}`);
      const detail = await api.get(`/api/admin/social-studio/scripts/${activeScript.script_id}`);
      setActiveScript(detail.data.script);
    } catch (e) {
      toast.error('Select failed');
    }
  };

  const handleDeleteScript = async (scriptId) => {
    if (!window.confirm('Delete this script permanently?')) return;
    await api.delete(`/api/admin/social-studio/scripts/${scriptId}`);
    setScripts((prev) => prev.filter((s) => s.script_id !== scriptId));
    if (activeScript?.script_id === scriptId) setActiveScript(null);
    toast.success('Deleted');
  };

  const handleSaveStatus = async (scriptId, status) => {
    await api.patch(`/api/admin/social-studio/scripts/${scriptId}`, { status });
    refreshScripts();
    if (activeScript?.script_id === scriptId) {
      setActiveScript({ ...activeScript, status });
    }
    toast.success('Marked ' + status);
  };

  const handleGenHooks = async () => {
    if (!hookForm.niche || !hookForm.audience || !hookForm.topic) {
      toast.error('Niche, audience, and topic are required');
      return;
    }
    setHooksLoading(true);
    try {
      const res = await api.post('/api/admin/social-studio/generate-hooks', hookForm);
      setHooks(res.data.hooks || []);
    } catch (e) {
      toast.error('Hooks failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setHooksLoading(false);
    }
  };

  const handleGenIdeas = async () => {
    if (!ideaForm.niche || !ideaForm.audience) {
      toast.error('Niche and audience are required');
      return;
    }
    setIdeasLoading(true);
    try {
      const res = await api.post('/api/admin/social-studio/generate-ideas', ideaForm);
      setIdeas(res.data.ideas || []);
    } catch (e) {
      toast.error('Ideas failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIdeasLoading(false);
    }
  };


  const selectedVariant = useMemo(() => {
    if (!activeScript) return null;
    const arr = activeScript.variants || [];
    const idx = Math.min(activeScript.selected_variant_idx || 0, arr.length - 1);
    return arr[idx] || null;
  }, [activeScript]);

  // Highest overall-viral-score variant (for the one-click "jump to best" chip).
  const bestVariant = useMemo(() => {
    const arr = activeScript?.variants || [];
    let idx = -1, score = -1;
    arr.forEach((v, i) => {
      const s = Number(v?.viral_scores?.overall || 0);
      if (s > score) { score = s; idx = i; }
    });
    return score > 0 ? { idx, score } : null;
  }, [activeScript]);

  const inner = (
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="social-studio-page">
        <div className="mb-4">
          <h1 className="text-3xl sm:text-4xl font-black text-[#0A0F1C] tracking-tight" data-testid="social-studio-title">
            Social Media Viral Studio
          </h1>
          <p className="text-sm text-[#475569] mt-1">
            Your full content suite — research, write, design &amp; publish. Switch between Content Intelligence, Video, Post and Flyer studios below.
          </p>
        </div>

        <StudioPlanBar memberView={embedded} />

        {limitNudge && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3" data-testid="studio-limit-nudge">
            <div className="flex items-center gap-2 text-sm text-amber-900">
              <Sparkles size={16} className="text-amber-500 flex-shrink-0" aria-hidden="true" />
              <span>{limitNudge.message || "You've reached your daily AI limit on this plan. Upgrade to keep creating, or come back tomorrow — limits reset daily."}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {limitNudge.can_upgrade && (
                <button type="button" onClick={() => { try { window.dispatchEvent(new CustomEvent('studio-upgrade-request')); } catch (_e) { /* noop */ } }} data-testid="studio-limit-upgrade" className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600">Upgrade plan</button>
              )}
              <button type="button" onClick={() => setLimitNudge(null)} aria-label="Dismiss" className="text-amber-700 hover:text-amber-900"><X size={16} /></button>
            </div>
          </div>
        )}

        {/* ===== Content Suite switcher (Content Intelligence · Video · Post · Flyer) ===== */}
        <StudioSuiteNav active={studioMode} embedded={embedded}
          onSelectMode={(m) => {
            if (m === 'video') { setStudioMode('video'); setTab('generator'); }
            else { setStudioMode('post'); setTab('post_studio'); setPostSubTab('generator'); }
          }} />

        {/* ===== Cross-platform preview grid (after "Convert to all 4 platforms") ===== */}
        {convertAllResults && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="convert-all-modal" onClick={() => setConvertAllResults(null)}>
            <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#0A0F1C]">Your cross-platform posts</h2>
                  <p className="text-xs text-[#64748B] mt-0.5">Review & copy each version. All are saved in Post Studio → Library.</p>
                </div>
                <button type="button" onClick={() => setConvertAllResults(null)} data-testid="convert-all-modal-close" aria-label="Close" className="text-[#64748B] hover:text-[#0A0F1C]"><X size={20} /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {convertAllResults.map((res) => {
                  const v = (res.post?.variants || [])[res.post?.selected_variant_idx || 0] || {};
                  const body = v.full_post || v.full_script || '';
                  const tags = (v.hashtags || []).join(' ');
                  return (
                    <div key={res.key} data-testid={`convert-all-card-${res.key}`} className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col">
                      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#1D4ED8]">{res.label}</span>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => copy(body)} data-testid={`convert-all-copy-post-${res.key}`} className="text-[11px] font-bold text-[#475569] hover:text-[#1D4ED8] hover:underline">Copy post</button>
                          {tags && <button type="button" onClick={() => copy(tags)} data-testid={`convert-all-copy-tags-${res.key}`} className="text-[11px] font-bold text-[#1D4ED8] hover:underline">Copy #tags</button>}
                          <button type="button" onClick={() => copy([body, tags].filter(Boolean).join('\n\n'))} data-testid={`convert-all-copy-all-${res.key}`} className="text-[11px] font-bold text-white bg-[#1D4ED8] px-2 py-0.5 rounded-full hover:opacity-90">Copy all</button>
                        </div>
                      </div>
                      <div className="text-sm text-[#0A0F1C] whitespace-pre-wrap flex-1 max-h-56 overflow-y-auto">{body || '(no content)'}</div>
                      {tags && <div className="text-[13px] text-[#1D4ED8] mt-2 break-words">{tags}</div>}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end pt-2 border-t border-[#F1F5F9]">
                <button type="button" onClick={() => { setStudioMode('post'); setTab('post_studio'); setPostSubTab('library'); setConvertAllResults(null); }} data-testid="convert-all-open-library" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90">
                  Open in Post Library →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#E2E8F0] overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="social-studio-tabs">
          {(studioMode === 'video' ? VIDEO_TABS : POST_TABS).map((t) => {
            const Icon = t.icon;
            const active = t.sub ? (tab === 'post_studio' && postSubTab === t.sub) : (tab === t.key && (studioMode === 'video' || ['hook_engine', 'planner', 'trends', 'creator', 'performance', 'ideas', 'brands'].includes(t.key)));
            const handle = () => {
              if (t.sub) {
                setTab('post_studio');
                setPostSubTab(t.sub);
              } else {
                setTab(t.key);
              }
            };
            return (
              <button
                key={t.key}
                type="button"
                onClick={handle}
                data-testid={`tab-${t.key.replace(':', '-')}`}
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap ${active ? 'text-[#1D4ED8] border-[#1D4ED8]' : 'text-[#475569] border-transparent hover:text-[#0A0F1C]'}`}
              >
                <Icon size={16} aria-hidden="true" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ===== Generator Tab ===== */}
        {tab === 'generator' && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="generator-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">1. Pick Platform & Brand</h2>

              <div>
                <label htmlFor="platform-select" className="block text-xs font-semibold text-[#475569] mb-1">Platform</label>
                <div className="flex flex-wrap gap-2" data-testid="platform-buttons">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setPlatform(p.key)}
                      data-testid={`platform-${p.key}`}
                      aria-pressed={platform === p.key}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${platform === p.key ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="brand-select" className="block text-xs font-semibold text-[#475569] mb-1">Brand Profile</label>
                <select
                  id="brand-select"
                  data-testid="brand-profile-select"
                  value={profileId}
                  onChange={(e) => setProfileId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                >
                  <option value="">- Quick fill (no saved profile) -</option>
                  {groupedProfileOptions(profiles)}
                </select>
              </div>

              {!profileId && (
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Niche (e.g. Final Expense Insurance)"
                    aria-label="Niche"
                    value={profileForm.niche}
                    onChange={(e) => setProfileForm({ ...profileForm, niche: e.target.value })}
                    className="px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                    data-testid="quickfill-niche"
                  />
                  <input
                    type="text"
                    placeholder="Target audience"
                    aria-label="Target audience"
                    value={profileForm.ideal_viewer}
                    onChange={(e) => setProfileForm({ ...profileForm, ideal_viewer: e.target.value })}
                    className="px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                    data-testid="quickfill-audience"
                  />
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">2. Your Idea (speak or type)</h2>

              <div className="bg-[#EFF6FF] border border-[#DBEAFE] rounded-xl p-3 space-y-2" data-testid="smart-fill-card">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={12} className="text-[#1D4ED8]" aria-hidden="true" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8]">Only have a rough idea?</span>
                  </div>
                  <span className="text-[10px] text-[#475569]">Drop your raw observation - AI fills topic, goal, tones & context.</span>
                </div>
                <textarea
                  rows={2}
                  value={rawIdea}
                  onChange={(e) => setRawIdea(e.target.value)}
                  placeholder='e.g. "Spirit Airlines filed bankruptcy, 17,000 lost their jobs. Job security is a myth - people need additional income streams."'
                  aria-label="Raw idea"
                  data-testid="smart-fill-input"
                  className="w-full px-3 py-2 border border-[#DBEAFE] rounded-lg text-xs bg-white"
                />
                <button
                  type="button"
                  onClick={handleSmartFill}
                  disabled={smartFilling || rawIdea.trim().length < 6}
                  data-testid="smart-fill-button"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1D4ED8] text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                >
                  {smartFilling ? <RefreshCw size={12} className="animate-spin" aria-hidden="true" /> : <Sparkles size={12} aria-hidden="true" />}
                  {smartFilling ? 'Expanding...' : 'Smart Fill from Idea'}
                </button>
                {smartFillResult && (
                  <p className="text-[10px] text-[#475569] italic" data-testid="smart-fill-why">Why this works: {smartFillResult.why_it_works}</p>
                )}
              </div>

              <div>
                <label htmlFor="topic-input" className="block text-xs font-semibold text-[#475569] mb-1">Video topic / angle</label>
                <input
                  id="topic-input"
                  type="text"
                  placeholder={`e.g. "Why your kids shouldn't pay for your funeral"`}
                  aria-label="Video topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                  data-testid="topic-input"
                />
              </div>

              <div>
                <label htmlFor="goal-input" className="block text-xs font-semibold text-[#475569] mb-1">Goal / CTA intent</label>
                <input
                  id="goal-input"
                  type="text"
                  placeholder="e.g. book a 15-min consult, drive comments, build email list"
                  aria-label="Goal"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                  data-testid="goal-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="context-input" className="text-xs font-semibold text-[#475569]">Context / story / talking points (optional)</label>
                  <button
                    type="button"
                    onClick={voice.listening ? voice.stop : voice.start}
                    data-testid="voice-toggle-button"
                    aria-pressed={voice.listening}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full border transition ${voice.listening ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'}`}
                  >
                    {voice.listening ? <MicOff size={12} aria-hidden="true" /> : <Mic size={12} aria-hidden="true" />}
                    {voice.listening ? 'Stop' : 'Voice'}
                  </button>
                </div>
                <textarea
                  id="context-input"
                  rows={5}
                  placeholder="Tell the AI your situation, the story, the angle, who you're speaking to..."
                  aria-label="Context"
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                  data-testid="context-input"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1">Tone(s) - pick any (multi-select)</label>
                <div className="flex flex-wrap gap-1.5" data-testid="tone-chips">
                  {TONES.map((t) => {
                    const active = selectedTones.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() =>
                          setSelectedTones((prev) =>
                            prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                          )
                        }
                        data-testid={`tone-chip-${t}`}
                        aria-pressed={active}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition ${
                          active
                            ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]'
                            : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#1D4ED8]'
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {availablePillars.length > 0 && (
                <div>
                  <label htmlFor="pillar-select" className="block text-xs font-semibold text-[#475569] mb-1">
                    Content pillar (from {selectedProfile?.name})
                  </label>
                  <select
                    id="pillar-select"
                    data-testid="content-pillar-select"
                    value={contentPillar}
                    onChange={(e) => setContentPillar(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                  >
                    <option value="">- Any / not specified -</option>
                    {availablePillars.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-[#64748B]">Need an idea? Start from a proven hook.</span>
                <HookLibraryPicker onPick={(h) => setTopic(h)} testidPrefix="gen-hook-library" />
              </div>

              <ViralFormatPicker
                formats={viralFormats}
                value={{ category: formatCategory, subtype: formatSubtype, faceless: formatFaceless, textOnScreen: formatTextOnScreen }}
                onChange={(v) => { setFormatCategory(v.category); setFormatSubtype(v.subtype); setFormatFaceless(v.faceless); setFormatTextOnScreen(v.textOnScreen); }}
                onSuggest={() => apiSuggestFormat({ niche: selectedProfile?.niche || profileForm.niche, audience: selectedProfile?.ideal_viewer || profileForm.ideal_viewer, topic: topic.trim() })}
                nicheAvailable={!!(selectedProfile?.niche || profileForm.niche?.trim())}
                hintText="Tip: pick a brand profile or enter a niche above to let AI choose a format for you."
              />

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label htmlFor="variants-input" className="text-xs font-semibold text-[#475569]">Variants:</label>
                  <input
                    id="variants-input"
                    type="number"
                    min="1"
                    max="5"
                    value={variants}
                    onChange={(e) => setVariants(Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1)))}
                    className="w-16 px-2 py-1 border border-[#E2E8F0] rounded-lg text-sm"
                    data-testid="variants-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || !topic.trim()}
                  data-testid="generate-script-button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1D4ED8] text-white font-semibold rounded-lg text-sm hover:bg-[#1641b5] disabled:opacity-50"
                >
                  {generating ? <RefreshCw size={16} className="animate-spin" aria-hidden="true" /> : <Sparkles size={16} aria-hidden="true" />}
                  {generating ? 'Generating...' : 'Generate viral script'}
                </button>
              </div>
              <GenerationProgress active={generating} />
            </div>
          </div>
        )}

        {/* ===== Refine Tab ===== */}
        {tab === 'refine' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="refine-panel">
            <div className="lg:col-span-2 space-y-4">
              {!activeScript && (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <Wand2 size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Generate a script first, or pick one from the Library.</p>
                </div>
              )}
              {activeScript && selectedVariant && (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="active-script-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="inline-flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">
                          {PLATFORMS.find((p) => p.key === activeScript.platform)?.label || activeScript.platform}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">
                          v{(activeScript.selected_variant_idx || 0) + 1} of {(activeScript.variants || []).length}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-[#0A0F1C]" data-testid="active-script-title">{selectedVariant.title || 'Untitled'}</h2>
                      <p className="text-xs text-[#64748B] mt-1">{activeScript.topic}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(activeScript.variants || []).map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleSelectVariant(i)}
                          aria-pressed={(activeScript.selected_variant_idx || 0) === i}
                          data-testid={`select-variant-${i}`}
                          className={`w-8 h-8 rounded-full text-xs font-bold border ${(activeScript.selected_variant_idx || 0) === i ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedVariant._unparsed ? (
                    <pre className="text-xs whitespace-pre-wrap bg-[#F8FAFC] p-3 rounded-lg border border-[#E2E8F0]">{selectedVariant.full_script}</pre>
                  ) : (
                    <div className="space-y-3" data-testid="script-breakdown">
                      <ScriptSection label="HOOK (0-3s)" body={selectedVariant.hook} color="#DC2626" />
                      <ScriptSection label="PROBLEM (3-13s)" body={selectedVariant.problem} color="#D97706" />
                      <ScriptSection label="SOLUTION / VALUE (13-50s)" body={selectedVariant.solution} color="#059669" />
                      <ScriptSection label="CTA (50-60s)" body={selectedVariant.cta} color="#1D4ED8" />

                      {selectedVariant.on_screen_text?.length > 0 && (
                        <Pillbox label="On-screen text overlays" items={selectedVariant.on_screen_text} />
                      )}
                      {selectedVariant.b_roll_suggestions?.length > 0 && (
                        <Pillbox label="B-roll / shot list" items={selectedVariant.b_roll_suggestions} />
                      )}
                      {selectedVariant.caption && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Caption</div>
                          <div className="text-sm text-[#0A0F1C] whitespace-pre-wrap bg-[#F8FAFC] p-3 rounded-lg border border-[#E2E8F0]" data-testid="script-caption">{selectedVariant.caption}</div>
                        </div>
                      )}
                      {selectedVariant.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {selectedVariant.hashtags.map((h) => (
                            <span key={h} className="text-xs px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full font-medium">{h}</span>
                          ))}
                        </div>
                      )}
                      {selectedVariant.estimated_seconds && (
                        <div className="text-xs text-[#94A3B8]">~{selectedVariant.estimated_seconds}s total</div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#F1F5F9]">
                    <button
                      type="button"
                      onClick={() => copy(buildFullScriptText(selectedVariant))}
                      data-testid="copy-script-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg hover:bg-[#E2E8F0]"
                    >
                      <Copy size={12} aria-hidden="true" /> Copy full script
                    </button>
                    <button
                      type="button"
                      onClick={() => copy(buildTeleprompterText(selectedVariant))}
                      data-testid="copy-teleprompter-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg hover:bg-[#E2E8F0]"
                    >
                      <Copy size={12} aria-hidden="true" /> Copy for teleprompter
                    </button>
                    <button
                      type="button"
                      onClick={() => { downloadTxt(selectedVariant.title || activeScript?.topic || 'script', toTeleprompterText(buildTeleprompterText(selectedVariant))); toast.success('Teleprompter .txt downloaded'); }}
                      data-testid="download-teleprompter-txt-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#FFF7ED] text-[#C2410C] rounded-lg hover:bg-[#FFEDD5]"
                    >
                      <Download size={12} aria-hidden="true" /> Download .txt
                    </button>
                    {!shareUrl ? (
                      <button
                        type="button"
                        onClick={handleCreateShare}
                        data-testid="share-script-button"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#EFF6FF] text-[#1D4ED8] rounded-lg hover:bg-[#DBEAFE]"
                      >
                        <Sparkles size={12} aria-hidden="true" /> Share to my team
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => copy(shareUrl)}
                          data-testid="copy-share-link-button"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#EFF6FF] text-[#1D4ED8] rounded-lg hover:bg-[#DBEAFE]"
                          title={shareUrl}
                        >
                          <Copy size={12} aria-hidden="true" /> Copy share link
                        </button>
                        <button
                          type="button"
                          onClick={handleRevokeShare}
                          data-testid="revoke-share-button"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-700 rounded-lg hover:bg-red-100"
                        >
                          Revoke
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleSaveStatus(activeScript.script_id, 'scheduled')}
                      data-testid="mark-scheduled"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                    >
                      <Save size={12} aria-hidden="true" /> Mark scheduled
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveStatus(activeScript.script_id, 'published')}
                      data-testid="mark-published"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100"
                    >
                      <Save size={12} aria-hidden="true" /> Mark published
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteScript(activeScript.script_id)}
                      data-testid="delete-script"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-700 rounded-lg hover:bg-red-100 ml-auto"
                    >
                      <Trash2 size={12} aria-hidden="true" /> Delete
                    </button>
                  </div>
                </div>
              )}

              {activeScript && selectedVariant && (
                <div className="bg-[#0A0F1C] text-white rounded-2xl p-6 space-y-3" data-testid="teleprompter-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen size={16} className="text-[#60A5FA]" aria-hidden="true" />
                      <h2 className="text-sm font-bold uppercase tracking-wider">Teleprompter script</h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => copy(buildTeleprompterText(selectedVariant))}
                      data-testid="teleprompter-copy-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/20 rounded-lg"
                    >
                      <Copy size={12} aria-hidden="true" /> Copy
                    </button>
                  </div>
                  <p className="text-[11px] text-white/50">Just the words to read — hook, problem, solution, CTA. No b-roll, overlays or props. Paste straight into your teleprompter.</p>
                  <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-white/90" data-testid="teleprompter-text">{buildTeleprompterText(selectedVariant)}</div>
                </div>
              )}

              {activeScript && selectedVariant?.viral_scores && (
                <ViralScoreCard scores={selectedVariant.viral_scores} />
              )}

              {activeScript && selectedVariant && (
                <ReelChecklistCard />
              )}

              {activeScript && selectedVariant?.compliance && (
                <ComplianceCard compliance={selectedVariant.compliance} onRemoveFlags={handleRemoveFlags} removing={isPending('remove-flagged-phrases')} />
              )}

              {activeScript && selectedVariant?.delivery_coach && (
                <DeliveryCoachCard coach={selectedVariant.delivery_coach} />
              )}

              {activeScript && selectedVariant && (
                <CaptionsSeoCard variant={selectedVariant} onCopy={copy} onGeneratePlatformCaptions={handlePlatformCaptions} generatingCaptions={isPending('platform-captions')} />
              )}

              {activeScript && selectedVariant && (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="transformations-card">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Zap size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Transformations</h2>
                    {anyPending && <RefreshCw size={12} className="animate-spin text-[#1D4ED8]" aria-hidden="true" />}
                    <span
                      data-testid="variant-count-chip"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EFF6FF] text-[#1D4ED8] border border-[#DBEAFE]"
                      title="AI variants generated for this script"
                    >
                      <Layers size={11} aria-hidden="true" /> {(activeScript.variants || []).length} variant{(activeScript.variants || []).length === 1 ? '' : 's'}
                    </span>
                    {bestVariant && bestVariant.idx !== (activeScript.selected_variant_idx || 0) && (
                      <button
                        type="button"
                        onClick={() => handleSelectVariant(bestVariant.idx)}
                        data-testid="jump-to-best-variant"
                        title={`Switch to the highest-scoring variant (v${bestVariant.idx + 1}, ${bestVariant.score} viral)`}
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        <TrendingUp size={11} aria-hidden="true" /> Jump to best (v{bestVariant.idx + 1} · {bestVariant.score})
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-[#64748B]">Each transformation adds a new variant - your originals stay in history.</p>
                  {anyPending && (
                    <div data-testid="transform-working-banner" className="flex items-center gap-2 rounded-lg bg-[#1D4ED8]/10 border border-[#1D4ED8]/20 px-3 py-2 text-xs font-semibold text-[#1D4ED8]">
                      <RefreshCw size={13} className="animate-spin shrink-0" aria-hidden="true" />
                      Working… you can start other transforms while this runs. Each button stays locked until it finishes.
                    </div>
                  )}

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Humanize level</div>
                    <div className="flex flex-wrap gap-1.5">
                      {[1, 2, 3, 4].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setHumanizeLevel(lvl)}
                          data-testid={`humanize-level-${lvl}`}
                          aria-pressed={humanizeLevel === lvl}
                          className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border ${humanizeLevel === lvl ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}
                        >
                          L{lvl} {['Light', 'Medium', 'Very natural', 'Raw'][lvl - 1]}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleHumanize(humanizeLevel)}
                        disabled={isPending('humanize')}
                        data-testid="humanize-apply-button"
                        className="ml-2 inline-flex items-center gap-1 px-3 py-1 text-[11px] font-bold rounded-full bg-[#1D4ED8] text-white disabled:opacity-60"
                      >
                        {isPending('humanize')
                          ? (<><RefreshCw size={11} className="animate-spin" aria-hidden="true" /> Working…</>)
                          : (<><Heart size={11} aria-hidden="true" /> Humanize</>)}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleMakeCompliant}
                      disabled={isPending('make-compliant')}
                      data-testid="make-compliant-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 hover:bg-emerald-100 disabled:opacity-60"
                    >
                      {isPending('make-compliant')
                        ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                        : (<><ShieldCheck size={12} aria-hidden="true" /> Make compliant</>)}
                    </button>
                    <button
                      type="button"
                      onClick={handleMakeMoreViral}
                      disabled={isPending('make-more-viral')}
                      data-testid="make-more-viral-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-fuchsia-50 text-fuchsia-700 rounded-lg border border-fuchsia-200 hover:bg-fuchsia-100 disabled:opacity-60"
                    >
                      {isPending('make-more-viral')
                        ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                        : (<><TrendingUp size={12} aria-hidden="true" /> Make more viral</>)}
                    </button>
                    <button
                      type="button"
                      onClick={handleMakeExtended}
                      disabled={isPending('make-extended')}
                      data-testid="make-extended-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 hover:bg-indigo-100 disabled:opacity-60"
                    >
                      {isPending('make-extended')
                        ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                        : (<><Zap size={12} aria-hidden="true" /> Make it extended (2.5-3 min)</>)}
                    </button>
                    <button
                      type="button"
                      onClick={handleImproveLowScores}
                      disabled={isPending('improve-low-scores')}
                      data-testid="improve-low-scores-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-amber-50 text-amber-700 rounded-lg border border-amber-200 hover:bg-amber-100 disabled:opacity-60"
                    >
                      {isPending('improve-low-scores')
                        ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                        : (<><Gauge size={12} aria-hidden="true" /> Improve low scores</>)}
                    </button>
                    {activeScript && activeScript.script_id && (
                      <div className="inline-flex items-center gap-1.5">
                        <select
                          value={convertToPostPlatform}
                          onChange={(e) => setConvertToPostPlatform(e.target.value)}
                          aria-label="Convert target platform"
                          data-testid="convert-post-platform"
                          className="px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs bg-white"
                        >
                          <option value="facebook">Facebook</option>
                          <option value="linkedin">LinkedIn</option>
                          <option value="instagram">Instagram</option>
                          <option value="x_twitter">X / Twitter</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => handleConvertScriptToPost(activeScript.script_id, convertToPostPlatform, 'medium')}
                          disabled={isPending('convert-to-post')}
                          data-testid="convert-script-to-post"
                          title="Reimagine this video script as a text post for the chosen platform"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg hover:opacity-90 disabled:opacity-60"
                        >
                          {isPending('convert-to-post')
                            ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                            : (<><Sparkles size={12} aria-hidden="true" /> Convert to text post →</>)}
                        </button>
                        <button
                          type="button"
                          onClick={handleConvertToAllPlatforms}
                          disabled={isPending('convert-all')}
                          data-testid="convert-all-platforms"
                          title="Generate Facebook, LinkedIn, Instagram & X text-post versions in one go"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90 disabled:opacity-60"
                        >
                          {isPending('convert-all')
                            ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Converting… ({convertAllProgress?.done ?? 0}/{convertAllProgress?.total ?? 4})</>)
                            : (<><Layers size={12} aria-hidden="true" /> All 4 platforms</>)}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Rewrite hook (style)</div>
                      <div className="flex gap-2">
                        <select
                          value={hookStyle}
                          onChange={(e) => setHookStyle(e.target.value)}
                          aria-label="Hook style"
                          data-testid="hook-style-select"
                          className="flex-1 px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs"
                        >
                          {HOOK_STYLES.map((h) => (
                            <option key={h.key} value={h.key}>{h.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => handleRewriteHook(hookStyle)}
                          disabled={isPending('rewrite-hook')}
                          data-testid="rewrite-hook-button"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg disabled:opacity-60"
                        >
                          {isPending('rewrite-hook')
                            ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                            : (<><Pencil size={12} aria-hidden="true" /> Rewrite</>)}
                        </button>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Rewrite CTA (intent)</div>
                      <div className="flex gap-2">
                        <select
                          value={ctaIntent}
                          onChange={(e) => setCtaIntent(e.target.value)}
                          aria-label="CTA intent"
                          data-testid="cta-intent-select"
                          className="flex-1 px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-xs"
                        >
                          {CTA_INTENTS.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => handleRewriteCta(ctaIntent)}
                          disabled={isPending('rewrite-cta')}
                          data-testid="rewrite-cta-button"
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg disabled:opacity-60"
                        >
                          {isPending('rewrite-cta')
                            ? (<><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Working…</>)
                            : (<><Pencil size={12} aria-hidden="true" /> Rewrite</>)}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeScript?.profile_id && (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="learn-card">
                  <div className="flex items-center gap-2">
                    <BookOpen size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Learn from this script</h2>
                  </div>
                  <p className="text-xs text-[#64748B]">Teach your brand profile what worked, what to avoid, what to remember.</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => handleLearnFromScript('sound_more_like')}
                      data-testid="learn-sound-more-like"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#EFF6FF] text-[#1D4ED8] rounded-lg border border-[#DBEAFE] hover:bg-[#DBEAFE]"
                    >
                      Sound more like this
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLearnFromScript('avoid_this_style')}
                      data-testid="learn-avoid-this-style"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-700 rounded-lg border border-red-200 hover:bg-red-100"
                    >
                      Avoid this style
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLearnFromScript('save_phrase')}
                      data-testid="learn-save-phrase"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg hover:bg-[#E2E8F0]"
                    >
                      Save this hook as a phrase
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLearnFromScript('save_cta')}
                      data-testid="learn-save-cta"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#F1F5F9] text-[#0A0F1C] rounded-lg hover:bg-[#E2E8F0]"
                    >
                      Save this CTA
                    </button>
                  </div>
                </div>
              )}

              {activeScript?.platform_reasoning && (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-4" data-testid="platform-reasoning">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Platform reasoning</div>
                  <p className="text-xs text-[#475569] leading-relaxed">{activeScript.platform_reasoning}</p>
                </div>
              )}

              {activeScript && (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4" data-testid="tags-editor">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Tags</div>
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="tags-chips">
                    {(activeScript.tags || []).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">
                        {t}
                        <button type="button" aria-label={`Remove tag ${t}`}
                          onClick={() => handleRemoveTag(t)}
                          data-testid={`remove-tag-${t}`}
                          className="text-[#1D4ED8] hover:text-red-600">×</button>
                      </span>
                    ))}
                    {!(activeScript.tags || []).length && <span className="text-[10px] text-[#94A3B8]">No tags yet</span>}
                  </div>
                  <input type="text" placeholder="Add tag and press Enter (e.g. high-converting)"
                    aria-label="Add tag"
                    data-testid="tag-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        e.preventDefault();
                        const val = e.target.value;
                        e.target.value = '';
                        handleAddTag(val);
                      }
                    }}
                    className="w-full px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs" />
                </div>
              )}
            </div>

            {/* Refine chat */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Refine with AI</h2>
              <p className="text-xs text-[#64748B]">
                Tell the AI what to change. It will produce a new variant - your originals stay in the version history.
              </p>
              <textarea
                rows={6}
                placeholder='e.g. "Make the hook more pattern-interrupt. Use a specific dollar amount."'
                aria-label="Refinement feedback"
                value={refineFeedback}
                onChange={(e) => setRefineFeedback(e.target.value)}
                disabled={!activeScript}
                data-testid="refine-feedback-input"
                className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] disabled:bg-[#F8FAFC]"
              />
              <button
                type="button"
                onClick={handleRefine}
                disabled={refining || !activeScript || !refineFeedback.trim()}
                data-testid="refine-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0F1C] text-white font-semibold rounded-lg text-sm hover:bg-[#1E293B] disabled:opacity-50 w-full justify-center"
              >
                {refining ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Wand2 size={14} aria-hidden="true" />}
                {refining ? 'Refining...' : 'Refine'}
              </button>
              <div className="text-[10px] text-[#94A3B8]">Refinements: {activeScript?.refinement_count || 0}</div>
            </div>
          </div>
        )}

        {/* ===== Video Remix Tab (Round 5G) ===== */}
        {tab === 'video_remix' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="video-remix-panel">
            <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Wand2 size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Remix a video script</h2>
              </div>
              <p className="text-xs text-[#64748B]">
                Paste a YouTube/TikTok/IG/X link to auto-transcribe, OR paste a raw script. Then remix it into your voice with a new tone or angle.
              </p>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">Video URL</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="url"
                    value={vremixUrl}
                    onChange={(e) => setVremixUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or TikTok/IG/X link"
                    data-testid="video-remix-url-input"
                    className="flex-1 px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:border-[#1D4ED8]"
                  />
                  <button
                    type="button"
                    onClick={() => handleVideoIngest('remix')}
                    disabled={vremixLoading || !vremixUrl.trim()}
                    data-testid="video-remix-ingest-button"
                    className="px-3 py-2 bg-[#0A0F1C] text-white text-xs font-semibold rounded-lg hover:bg-[#1E293B] disabled:opacity-50"
                  >
                    {vremixLoading ? 'Ingesting...' : 'Ingest URL'}
                  </button>
                </div>
                {vremixIngestMeta && (
                  <div className="mt-2 text-[11px] text-[#475569] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="video-remix-ingest-meta">
                    <div><strong>{vremixIngestMeta.title || 'Untitled'}</strong> - {vremixIngestMeta.platform || '?'} - {vremixIngestMeta.duration || 0}s</div>
                    <div className="text-[#64748B] mt-0.5">Transcript: {vremixIngestMeta.transcript_length || 0} chars</div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">Source script / transcript</label>
                <textarea
                  rows={8}
                  value={vremixSource}
                  onChange={(e) => setVremixSource(e.target.value)}
                  placeholder="Paste the original script or transcript here (or use Ingest URL above)..."
                  data-testid="video-remix-source-input"
                  className="w-full mt-1 px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm font-mono focus:ring-2 focus:ring-[#1D4ED8] focus:border-[#1D4ED8]"
                />
                <div className="text-[10px] text-[#94A3B8] mt-1">{vremixSource.length} characters (minimum 40)</div>
              </div>
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">Target platform</label>
                <select
                  value={vremixPlatform}
                  onChange={(e) => setVremixPlatform(e.target.value)}
                  data-testid="video-remix-platform-select"
                  className="w-full mt-1 px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:border-[#1D4ED8]"
                >
                  {SINGLE_PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">Target tone (optional)</label>
                <input
                  type="text"
                  list="vremix-tone-list"
                  value={vremixTone}
                  onChange={(e) => setVremixTone(e.target.value)}
                  placeholder="e.g. friendly-conversational"
                  data-testid="video-remix-tone-input"
                  className="w-full mt-1 px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:border-[#1D4ED8]"
                />
                <datalist id="vremix-tone-list">
                  {TONES.map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">Angle shift (optional)</label>
                <input
                  type="text"
                  value={vremixAngle}
                  onChange={(e) => setVremixAngle(e.target.value)}
                  placeholder="e.g. retell from the contractor's POV"
                  data-testid="video-remix-angle-input"
                  className="w-full mt-1 px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:border-[#1D4ED8]"
                />
              </div>

              <button
                type="button"
                onClick={handleVideoRemix}
                disabled={vremixLoading || (vremixSource.trim().length < 40 && !vremixUrl.trim())}
                data-testid="video-remix-submit-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0F1C] text-white font-semibold rounded-lg text-sm hover:bg-[#1E293B] disabled:opacity-50 w-full justify-center"
              >
                {vremixLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Wand2 size={14} aria-hidden="true" />}
                {vremixLoading ? 'Remixing...' : 'Remix in my voice'}
              </button>

              {(vremixVariants || []).length > 0 && (
                <div className="border-t border-[#E2E8F0] pt-3 space-y-3" data-testid="video-remix-variants">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#475569]">{vremixVariants.length} variant(s)</div>
                  {vremixVariants.map((v, i) => (
                    <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 space-y-2">
                      <div className="text-xs font-bold text-[#0A0F1C]">{v.title || `Variant ${i + 1}`}</div>
                      {v.hook && <div className="text-[11px] text-[#475569]"><strong>Hook:</strong> {v.hook}</div>}
                      {v.script && <div className="text-[11px] text-[#64748B] whitespace-pre-wrap line-clamp-6">{v.script}</div>}
                      <button
                        type="button"
                        onClick={() => handleCarryOver({ type: 'plan', text: `${v.title || ''}\n\n${v.hook || ''}\n\n${v.script || ''}`.trim() })}
                        data-testid={`video-remix-carryover-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-[#CBD5E1] rounded text-[10px] font-semibold text-[#0A0F1C] hover:bg-[#F1F5F9]"
                      >
                        Carry over to Post Studio
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Hook Engine Tab (Round 2) ===== */}
        {tab === 'hook_engine' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="hook-engine-panel">
            <div className="lg:col-span-1 bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Hook Engine</h2>
              </div>
              <p className="text-xs text-[#64748B]">Generate categorised, scored hooks across 15 proven archetypes.</p>
              <select
                value={hookEngineForm.platform}
                onChange={(e) => setHookEngineForm({ ...hookEngineForm, platform: e.target.value })}
                aria-label="Hook engine platform"
                data-testid="hook-engine-platform"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
              >
                {PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <input type="text" placeholder="Niche" aria-label="Niche" value={hookEngineForm.niche}
                onChange={(e) => setHookEngineForm({ ...hookEngineForm, niche: e.target.value })}
                data-testid="hook-engine-niche"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <input type="text" placeholder="Audience" aria-label="Audience" value={hookEngineForm.audience}
                onChange={(e) => setHookEngineForm({ ...hookEngineForm, audience: e.target.value })}
                data-testid="hook-engine-audience"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <input type="text" placeholder="Topic" aria-label="Topic" value={hookEngineForm.topic}
                onChange={(e) => setHookEngineForm({ ...hookEngineForm, topic: e.target.value })}
                data-testid="hook-engine-topic"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">
                  Categories ({hookEngineSelected.length || hookEngineCategories.length})
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto" data-testid="hook-engine-categories">
                  {hookEngineCategories.map((c) => {
                    const active = hookEngineSelected.includes(c.key);
                    return (
                      <button key={c.key} type="button"
                        onClick={() => setHookEngineSelected((prev) => prev.includes(c.key) ? prev.filter((x) => x !== c.key) : [...prev, c.key])}
                        aria-pressed={active}
                        data-testid={`hook-engine-cat-${c.key}`}
                        className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${active ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                <button type="button" onClick={() => setHookEngineSelected([])} className="text-[10px] text-[#1D4ED8] mt-1 underline">Reset (all 15)</button>
              </div>

              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-semibold text-[#64748B]">Start from a proven hook</span>
                <HookLibraryPicker onPick={(h) => setHookEngineForm({ ...hookEngineForm, topic: h })} testidPrefix="he-hook-library" />
              </div>

              <ViralFormatPicker
                formats={viralFormats}
                value={heFormat}
                onChange={setHeFormat}
                onSuggest={() => apiSuggestFormat({ niche: hookEngineForm.niche, audience: hookEngineForm.audience, topic: hookEngineForm.topic })}
                nicheAvailable={!!hookEngineForm.niche?.trim()}
                prefix="he-format"
                containerTestId="he-viral-format-picker"
                hintText="Tip: enter a niche above to let AI choose a format."
              />

              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-[#475569]">Per category:</label>
                <input type="number" min="1" max="5" value={hookEngineForm.per_category}
                  onChange={(e) => setHookEngineForm({ ...hookEngineForm, per_category: Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 2)) })}
                  className="w-16 px-2 py-1 border border-[#E2E8F0] rounded-lg text-sm" />
              </div>
              <button type="button" onClick={handleHookEngineGenerate} disabled={hookEngineLoading}
                data-testid="hook-engine-generate"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                {hookEngineLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                {hookEngineLoading ? 'Generating...' : 'Generate hooks'}
              </button>

              <div className="pt-3 border-t border-[#F1F5F9] space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Score a single hook</div>
                <textarea rows={2} value={hookScoreText}
                  onChange={(e) => setHookScoreText(e.target.value)}
                  placeholder="Paste your hook to score it..."
                  aria-label="Hook to score"
                  data-testid="hook-score-input"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                <button type="button" onClick={handleScoreSingleHook} disabled={hookScoring}
                  data-testid="hook-score-button"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0F1C] text-white text-xs font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                  {hookScoring ? 'Scoring...' : 'Score it'}
                </button>
                {hookScoreResult && (
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2 space-y-1" data-testid="hook-score-result">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-[#0A0F1C]">{hookScoreResult.category}</span>
                      <span className="font-black text-lg" style={{ color: scoreColor(Number(hookScoreResult.scores?.overall || 0)) }}>
                        {hookScoreResult.scores?.overall || 0}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#475569]">{hookScoreResult.feedback}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3" data-testid="hook-engine-results">
              {hookEngineResults.length === 0 ? (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <Zap size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Pick categories, fill the form, then Generate.</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {hookEngineResults.map((h, i) => {
                    const overall = Number(h.scores?.overall || 0);
                    return (
                      <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid={`hook-result-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8]">{h.category}</span>
                          <span className="text-xl font-black" style={{ color: scoreColor(overall) }}>{overall}</span>
                        </div>
                        <p className="text-sm text-[#0A0F1C] font-semibold leading-snug">{h.text}</p>
                        <div className="flex gap-2 text-[10px] text-[#94A3B8]">
                          <span>Hook {h.scores?.hook_strength || 0}</span>
                          <span>Curiosity {h.scores?.curiosity || 0}</span>
                          <span>Retain {h.scores?.retention || 0}</span>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => copy(h.text)} className="text-[10px] text-[#475569] hover:text-[#1D4ED8] underline">Copy</button>
                          <button type="button" onClick={() => { setTopic(h.text); setTab('generator'); toast.success('Loaded as topic'); }} className="text-[10px] text-[#1D4ED8] underline">Use as topic</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Planner Tab (Round 2) ===== */}
        {tab === 'planner' && (
          <div className="space-y-6" data-testid="planner-panel">
            <DFYPlanGenerator api={api} profiles={profiles} onCreateBrand={() => setTab('brands')} />
            <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Camera size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">New Content Plan</h2>
              </div>
              <select value={planForm.profile_id} onChange={(e) => setPlanForm({ ...planForm, profile_id: e.target.value })}
                aria-label="Plan brand profile"
                data-testid="plan-profile"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                <option value="">- Pick brand profile -</option>
                {groupedProfileOptions(profiles)}
              </select>
              <input type="text" placeholder="Plan name (optional)" aria-label="Plan name" value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                data-testid="plan-name"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="plan-days" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Calendar length</label>
                  <select id="plan-days" value={planForm.days} onChange={(e) => setPlanForm({ ...planForm, days: parseInt(e.target.value, 10) })}
                    data-testid="plan-days"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="plan-posts-per-day" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Posts per day</label>
                  <input id="plan-posts-per-day" type="number" min="1" max="5" value={planForm.posts_per_day}
                    onChange={(e) => setPlanForm({ ...planForm, posts_per_day: parseInt(e.target.value, 10) || 1 })}
                    data-testid="plan-posts-per-day"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                </div>
              </div>
              <textarea rows={3} value={planForm.extra_notes}
                onChange={(e) => setPlanForm({ ...planForm, extra_notes: e.target.value })}
                aria-label="Extra notes"
                data-testid="plan-notes"
                placeholder="Notes (themes, seasonal events, offers running...)"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <button type="button" onClick={handleCreatePlan} disabled={planLoading || !planForm.profile_id}
                data-testid="create-plan-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                {planLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                {planLoading ? 'Generating...' : 'Generate plan'}
              </button>

              <div className="pt-3 border-t border-[#F1F5F9]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Saved plans ({plans.length})</div>
                <ul className="space-y-1 max-h-80 overflow-auto" data-testid="plan-list">
                  {plans.map((p) => (
                    <li key={p.plan_id} className="flex items-center gap-2 text-xs">
                      <button type="button" onClick={() => setActivePlan(p)} className="flex-1 text-left p-2 rounded hover:bg-[#F8FAFC]" data-testid={`open-plan-${p.plan_id}`}>
                        <div className="font-semibold text-[#0A0F1C]">{p.name}</div>
                        <div className="text-[#94A3B8]">{p.days}d · {p.profile_name} · {(p.items || []).length} items</div>
                      </button>
                      <button type="button" onClick={() => handleDeletePlan(p.plan_id)} aria-label="Delete plan" className="text-[#94A3B8] hover:text-red-600">
                        <Trash2 size={12} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-2">
              {!activePlan ? (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <Camera size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Generate a plan or pick one from the list.</p>
                </div>
              ) : (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="active-plan">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <h2 className="font-bold text-[#0A0F1C]">{activePlan.name}</h2>
                      <p className="text-xs text-[#64748B]">{activePlan.days} days · {activePlan.profile_name} · {(activePlan.items || []).length} items</p>
                    </div>
                    <button type="button" onClick={() => handleExportPlan(activePlan.plan_id)}
                      data-testid="export-plan-button"
                      className="text-xs font-semibold text-[#1D4ED8] hover:underline">Export CSV</button>
                  </div>
                  <ul className="divide-y divide-[#F1F5F9]" data-testid="plan-items">
                    {(activePlan.items || []).map((it) => (
                      <li key={it.item_id} className="py-3" data-testid={`plan-item-${it.item_id}`}>
                        <div className="flex items-start gap-3 flex-wrap">
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">Day {it.day}</span>
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">{PLATFORMS.find((p) => p.key === it.platform)?.label || it.platform}</span>
                          <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{it.pillar}</span>
                          <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{it.archetype}</span>
                          <select value={it.status || 'idea'} onChange={(e) => handleUpdatePlanItem(activePlan.plan_id, it.item_id, { status: e.target.value })}
                            aria-label="Status"
                            data-testid={`plan-item-status-${it.item_id}`}
                            className="ml-auto text-[10px] px-2 py-0.5 border border-[#E2E8F0] rounded-full">
                            <option value="idea">idea</option>
                            <option value="scripted">scripted</option>
                            <option value="scheduled">scheduled</option>
                            <option value="posted">posted</option>
                            <option value="skipped">skipped</option>
                          </select>
                        </div>
                        <div className="font-semibold text-sm text-[#0A0F1C] mt-1">{it.title}</div>
                        <div className="text-xs text-[#475569] italic">"{it.hook_line}"</div>
                        {it.description && <div className="text-xs text-[#64748B] mt-1">{it.description}</div>}
                        <div className="flex items-center gap-2 mt-2">
                          {it.script_id ? (
                            <span className="text-[10px] text-emerald-700 font-semibold">✓ Script generated</span>
                          ) : (
                            <button type="button" onClick={() => handleScriptFromPlanItem(activePlan.plan_id, it.item_id)} disabled={planLoading}
                              data-testid={`generate-script-from-item-${it.item_id}`}
                              className="text-[10px] text-[#1D4ED8] font-semibold underline disabled:opacity-50">
                              Generate full script →
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* ===== Repurpose Tab (Round 2) ===== */}
        {tab === 'repurpose' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="repurpose-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Repurposing Engine</h2>
              </div>
              <p className="text-xs text-[#64748B]">Paste a long-form transcript, blog, podcast or thread. We'll extract the highest-leverage short-form clips, ranked by viral score.</p>
              <select value={repurposeForm.source_type} onChange={(e) => setRepurposeForm({ ...repurposeForm, source_type: e.target.value })}
                aria-label="Source type"
                data-testid="repurpose-source-type"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                <option value="long_form">Long-form video transcript</option>
                <option value="podcast">Podcast episode</option>
                <option value="blog">Blog post</option>
                <option value="webinar">Webinar / talk</option>
                <option value="thread">Twitter thread</option>
              </select>
              <select value={repurposeForm.profile_id} onChange={(e) => setRepurposeForm({ ...repurposeForm, profile_id: e.target.value })}
                aria-label="Repurpose profile"
                data-testid="repurpose-profile"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                <option value="">- Optional brand profile -</option>
                {groupedProfileOptions(profiles)}
              </select>
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-[#475569]">Clips:</label>
                <input type="number" min="1" max="15" value={repurposeForm.count}
                  onChange={(e) => setRepurposeForm({ ...repurposeForm, count: parseInt(e.target.value, 10) || 5 })}
                  aria-label="Clip count"
                  data-testid="repurpose-count"
                  className="w-16 px-2 py-1 border border-[#E2E8F0] rounded-lg text-sm" />
              </div>
              <textarea rows={12} value={repurposeForm.source_text}
                onChange={(e) => setRepurposeForm({ ...repurposeForm, source_text: e.target.value })}
                aria-label="Source content"
                data-testid="repurpose-source-text"
                placeholder="Paste your transcript / article / thread here (≥ 80 chars)..."
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono" />
              <div className="text-[10px] text-[#94A3B8]">{repurposeForm.source_text.length} chars</div>
              <button type="button" onClick={handleRepurpose} disabled={repurposeLoading || repurposeForm.source_text.length < 80}
                data-testid="repurpose-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                {repurposeLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <TrendingUp size={14} aria-hidden="true" />}
                {repurposeLoading ? 'Extracting...' : 'Extract clips'}
              </button>
            </div>
            <div className="lg:col-span-2 space-y-3" data-testid="repurpose-clips">
              {repurposeClips.length === 0 ? (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <TrendingUp size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Paste source content and click "Extract clips".</p>
                </div>
              ) : (
                repurposeClips.map((c, i) => {
                  const score = Number(c.viral_score || 0);
                  return (
                    <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`repurpose-clip-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">
                              {PLATFORMS.find((p) => p.key === c.suggested_platform)?.label || c.suggested_platform}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{c.archetype}</span>
                          </div>
                          <h3 className="font-bold text-[#0A0F1C]">{c.title}</h3>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black" style={{ color: scoreColor(score) }}>{score}</div>
                          <div className="text-[10px] text-[#94A3B8]">viral</div>
                        </div>
                      </div>
                      <p className="text-sm text-[#0A0F1C] mt-2 italic">"{c.hook}"</p>
                      <p className="text-xs text-[#475569] mt-1">{c.promise}</p>
                      {c.distilled_point && <p className="text-xs text-[#64748B] mt-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">{c.distilled_point}</p>}
                      {c.why_it_works && <p className="text-[10px] text-emerald-700 mt-2"><strong>Why it works:</strong> {c.why_it_works}</p>}
                      <div className="flex gap-2 mt-3">
                        <button type="button" onClick={() => handleUseRepurposeClip(c)} data-testid={`use-clip-${i}`}
                          className="text-xs font-semibold text-[#1D4ED8] hover:underline">
                          Use in Generator →
                        </button>
                        <button type="button" onClick={() => copy(`${c.title}\n\nHOOK: ${c.hook}\n\nPROMISE: ${c.promise}\n\n${c.distilled_point}`)}
                          className="text-xs text-[#475569] hover:text-[#1D4ED8] underline">Copy</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ===== Trends Tab (Round 3) ===== */}
        {tab === 'trends' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="trends-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Trend Discovery</h2>
              </div>
              <input type="text" placeholder="Niche" aria-label="Niche" value={trendsForm.niche}
                onChange={(e) => setTrendsForm({ ...trendsForm, niche: e.target.value })}
                data-testid="trends-niche"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <input type="text" placeholder="Audience" aria-label="Audience" value={trendsForm.audience}
                onChange={(e) => setTrendsForm({ ...trendsForm, audience: e.target.value })}
                data-testid="trends-audience"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <input type="text" placeholder="Focus (e.g. hooks, sounds, formats)" aria-label="Focus" value={trendsForm.focus}
                onChange={(e) => setTrendsForm({ ...trendsForm, focus: e.target.value })}
                data-testid="trends-focus"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <textarea rows={2} placeholder="Competitor handles or URLs (comma/space separated)"
                aria-label="Competitor handles"
                data-testid="trends-handles"
                value={trendsForm.competitor_handles}
                onChange={(e) => setTrendsForm({ ...trendsForm, competitor_handles: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <textarea rows={5} placeholder="Paste captions / transcripts you noticed (optional)"
                aria-label="Manual paste"
                data-testid="trends-paste"
                value={trendsForm.manual_paste}
                onChange={(e) => setTrendsForm({ ...trendsForm, manual_paste: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono" />
              <button type="button" onClick={handleDiscoverTrends} disabled={trendsLoading || !trendsForm.niche}
                data-testid="trends-discover-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                {trendsLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <TrendingUp size={14} aria-hidden="true" />}
                {trendsLoading ? 'Analysing...' : 'Discover trends'}
              </button>

              <div className="pt-3 border-t border-[#F1F5F9]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Saved reports ({trends.length})</div>
                <ul className="space-y-1 max-h-80 overflow-auto" data-testid="trends-list">
                  {trends.map((t) => (
                    <li key={t.trend_id} className="flex items-center gap-2 text-xs">
                      <button type="button" onClick={() => setActiveTrend(t)} data-testid={`open-trend-${t.trend_id}`}
                        className="flex-1 text-left p-2 rounded hover:bg-[#F8FAFC]">
                        <div className="font-semibold text-[#0A0F1C]">{t.niche}</div>
                        <div className="text-[#94A3B8]">{new Date(t.created_at).toLocaleDateString()} · {(t.platforms || []).length} platforms</div>
                      </button>
                      <button type="button" onClick={() => handleDeleteTrend(t.trend_id)} aria-label="Delete trend" className="text-[#94A3B8] hover:text-red-600">
                        <Trash2 size={12} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3">
              {!activeTrend ? (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <TrendingUp size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Generate a trend report or pick a saved one.</p>
                </div>
              ) : (
                <TrendReportView trend={activeTrend} onCopy={copy} onUseHook={(h) => { setTopic(h); setTab('generator'); toast.success('Loaded as topic'); }} />
              )}
            </div>
          </div>
        )}

        {/* ===== Creator Analysis Tab (Round 3) ===== */}
        {tab === 'creator' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="creator-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <div className="flex items-center gap-2">
                <UserSquare2 size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Creator Analysis</h2>
              </div>
              <p className="text-xs text-[#64748B]">Paste a competitor's transcript or captions. We'll reverse-engineer their formula and rewrite it in your voice.</p>
              <input type="text" placeholder="Competitor handle (optional)" aria-label="Competitor handle"
                data-testid="creator-handle"
                value={creatorForm.competitor_handle}
                onChange={(e) => setCreatorForm({ ...creatorForm, competitor_handle: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <select value={creatorForm.your_profile_id}
                onChange={(e) => setCreatorForm({ ...creatorForm, your_profile_id: e.target.value })}
                aria-label="Your profile"
                data-testid="creator-profile"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                <option value="">- Your brand profile (for rewrites) -</option>
                {groupedProfileOptions(profiles)}
              </select>
              <textarea rows={10} placeholder="Paste competitor's transcript / captions (≥ 80 chars)..."
                aria-label="Competitor content"
                data-testid="creator-content"
                value={creatorForm.competitor_content}
                onChange={(e) => setCreatorForm({ ...creatorForm, competitor_content: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm font-mono" />
              <textarea rows={2} placeholder="Optional notes (what struck you)"
                aria-label="Notes"
                data-testid="creator-notes"
                value={creatorForm.notes}
                onChange={(e) => setCreatorForm({ ...creatorForm, notes: e.target.value })}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
              <button type="button" onClick={handleAnalyseCreator} disabled={creatorLoading || creatorForm.competitor_content.length < 80}
                data-testid="creator-analyse-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                {creatorLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Wand2 size={14} aria-hidden="true" />}
                {creatorLoading ? 'Analysing...' : 'Reverse-engineer'}
              </button>

              <div className="pt-3 border-t border-[#F1F5F9]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Saved analyses ({analyses.length})</div>
                <ul className="space-y-1 max-h-60 overflow-auto" data-testid="creator-list">
                  {analyses.map((a) => (
                    <li key={a.analysis_id} className="flex items-center gap-2 text-xs">
                      <button type="button" onClick={() => setActiveAnalysis(a)} data-testid={`open-analysis-${a.analysis_id}`}
                        className="flex-1 text-left p-2 rounded hover:bg-[#F8FAFC]">
                        <div className="font-semibold text-[#0A0F1C]">{a.competitor_handle || '(no handle)'}</div>
                        <div className="text-[#94A3B8]">{new Date(a.created_at).toLocaleDateString()}</div>
                      </button>
                      <button type="button" onClick={() => handleDeleteAnalysis(a.analysis_id)} aria-label="Delete analysis" className="text-[#94A3B8] hover:text-red-600">
                        <Trash2 size={12} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-3">
              {!activeAnalysis ? (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                  <UserSquare2 size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Run an analysis to see the breakdown.</p>
                </div>
              ) : (
                <CreatorAnalysisView analysis={activeAnalysis} onCopy={copy} onUseRewrite={handleUseRewrite} />
              )}
            </div>
          </div>
        )}

        {/* ===== Performance Tab (Round 3) ===== */}
        {tab === 'performance' && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="performance-panel">
            <div className="space-y-4">
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Gauge size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Log performance</h2>
                </div>
                {!activeScript ? (
                  <p className="text-xs text-[#64748B]">Pick a script from the Library first.</p>
                ) : (
                  <>
                    <p className="text-xs text-[#64748B]">
                      For: <strong>{(activeScript.variants?.[activeScript.selected_variant_idx || 0]?.title) || activeScript.topic}</strong>
                    </p>
                    <input type="date" aria-label="Posted at"
                      data-testid="perf-posted-at"
                      value={perfForm.posted_at}
                      onChange={(e) => setPerfForm({ ...perfForm, posted_at: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                    {['views', 'likes', 'comments', 'shares', 'saves', 'follows_gained', 'link_clicks'].map((k) => (
                      <div key={k} className="flex items-center gap-2">
                        <label className="text-xs font-semibold text-[#475569] capitalize w-28">{k.replace('_', ' ')}</label>
                        <input type="number" min="0"
                          value={perfForm[k]}
                          onChange={(e) => setPerfForm({ ...perfForm, [k]: parseInt(e.target.value, 10) || 0 })}
                          aria-label={k}
                          data-testid={`perf-${k}`}
                          className="flex-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-sm" />
                      </div>
                    ))}
                    <textarea rows={2} placeholder="Notes (optional)" aria-label="Notes"
                      data-testid="perf-notes"
                      value={perfForm.notes}
                      onChange={(e) => setPerfForm({ ...perfForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                    <button type="button" onClick={handleAddPerf} disabled={perfLoading}
                      data-testid="perf-add-button"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                      {perfLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
                      Save metric
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              {activeScript && (
                <PerformanceTimeline metrics={perfMetrics} totals={perfTotals} onDelete={handleDeletePerf} />
              )}

              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="perf-leaderboard">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C] mb-3">Top performers</h2>
                {perfSummary.length === 0 ? (
                  <p className="text-xs text-[#64748B]">No metrics logged yet. Start logging to see rankings.</p>
                ) : (
                  <ul className="divide-y divide-[#F1F5F9]">
                    {perfSummary.map((p, i) => (
                      <li key={p.script_id} className="py-2 flex items-center gap-3" data-testid={`leaderboard-row-${i}`}>
                        <span className="text-xs font-mono text-[#94A3B8] w-5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-[#0A0F1C] truncate">{p.title || p.topic || p.script_id}</div>
                          <div className="text-[10px] text-[#94A3B8]">
                            {(p.platforms || []).join(', ')} · {p.samples} sample{p.samples === 1 ? '' : 's'} · engagement {p.engagement_rate}%
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-[#0A0F1C]">{p.views.toLocaleString()}</div>
                          <div className="text-[10px] text-[#94A3B8]">views</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="insights-card">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">AI Insights</h2>
                  </div>
                  <div className="flex gap-2">
                    <input type="text" placeholder='Optional focus (e.g. "hook length")'
                      aria-label="Insights focus"
                      data-testid="insights-focus"
                      value={insightFocus}
                      onChange={(e) => setInsightFocus(e.target.value)}
                      className="px-3 py-1.5 border border-[#E2E8F0] rounded-lg text-xs w-48" />
                    <button type="button" onClick={handleGenerateInsights} disabled={insightsLoading || perfSummary.length === 0}
                      data-testid="generate-insights-button"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#1D4ED8] text-white text-xs font-semibold rounded-lg disabled:opacity-50">
                      {insightsLoading ? <RefreshCw size={12} className="animate-spin" aria-hidden="true" /> : <Sparkles size={12} aria-hidden="true" />}
                      {insightsLoading ? 'Analysing...' : 'What\'s working?'}
                    </button>
                  </div>
                </div>
                {!activeInsight ? (
                  <p className="text-xs text-[#64748B]">
                    {perfSummary.length === 0
                      ? 'Log a few metrics first, then ask the AI what patterns are working.'
                      : 'Click "What\'s working?" to compare your top vs bottom performers.'}
                  </p>
                ) : (
                  <InsightsView insight={activeInsight} />
                )}
                {insights.length > 0 && (
                  <div className="pt-3 mt-3 border-t border-[#F1F5F9]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Past insights ({insights.length})</div>
                    <ul className="space-y-1 max-h-32 overflow-auto" data-testid="insights-list">
                      {insights.map((ins) => (
                        <li key={ins.insight_id}>
                          <button type="button" onClick={() => setActiveInsight(ins)}
                            data-testid={`open-insight-${ins.insight_id}`}
                            className="text-xs text-left w-full p-2 rounded hover:bg-[#F8FAFC]">
                            <div className="font-semibold text-[#0A0F1C]">{ins.focus || 'General'}</div>
                            <div className="text-[10px] text-[#94A3B8]">{new Date(ins.created_at).toLocaleString()}</div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== Post Studio Tab (Round 5A) ===== */}
        {tab === 'post_studio' && (
          <div className="space-y-4" data-testid="post-studio-panel">
            {postSubTab === 'generator' && (
              <>
                {/* Connected flow: a video script you just made/refined can become a post here */}
                {activeScript && activeScript.script_id && (
                  <div className="bg-[#EFF6FF] border border-[#1E5BA8]/30 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="from-script-banner">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold text-[#1E5BA8] flex items-center gap-1.5"><Wand2 size={13} /> Video script ready</div>
                      <div className="text-[11px] text-[#475569] truncate mt-0.5">Turn "{activeScript.topic || 'your latest script'}" into a text post — same idea, rewritten for reading.</div>
                    </div>
                    <select value={convertPlatform} onChange={(e) => setConvertPlatform(e.target.value)} data-testid="from-script-platform" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs font-semibold bg-white">
                      {[['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn'], ['x_twitter', 'X / Twitter']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={convertLength} onChange={(e) => setConvertLength(e.target.value)} data-testid="from-script-length" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs font-semibold bg-white">
                      {[['short', 'Short'], ['medium', 'Medium'], ['long', 'Long']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button type="button" onClick={() => handleConvertScriptToPost(activeScript.script_id, convertPlatform, convertLength, true)} data-testid="from-script-create" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E5BA8] text-white text-xs font-bold rounded-lg hover:bg-[#1A4F94]"><Sparkles size={13} /> Create post from script</button>
                  </div>
                )}
                {/* One-click pipeline: idea → post → image → schedule */}
                <div className="bg-gradient-to-br from-[#0A0F1C] via-[#1E2B49] to-[#0A0F1C] text-white border border-[#1E2B49] rounded-2xl p-5 space-y-3" data-testid="pipeline-panel">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-fuchsia-300" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-white">One-click pipeline</h2>
                    <span className="text-[10px] uppercase tracking-wider text-white/60 ml-auto">idea → post → image → schedule</span>
                  </div>
                  <textarea rows={2} value={pipelineIdea} onChange={(e) => setPipelineIdea(e.target.value)}
                    placeholder="Drop a raw idea / observation - e.g. 'Spirit Airlines bankruptcy proves job security is a myth'"
                    aria-label="Pipeline idea"
                    data-testid="pipeline-idea"
                    className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40" />
                  <div className="flex flex-wrap gap-3 items-center text-[11px]">
                    <label className="inline-flex items-center gap-1.5 text-white/80">
                      <input type="checkbox" checked={pipelineGenerateImage}
                        onChange={(e) => setPipelineGenerateImage(e.target.checked)}
                        data-testid="pipeline-genimg" />
                      Auto-generate image
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-white/80">
                      <input type="checkbox" checked={pipelineSchedule}
                        onChange={(e) => setPipelineSchedule(e.target.checked)}
                        data-testid="pipeline-schedule" />
                      Auto-schedule
                    </label>
                    {pipelineSchedule && (
                      <>
                        <input type="date" value={pipelineDate} onChange={(e) => setPipelineDate(e.target.value)}
                          aria-label="Pipeline date"
                          data-testid="pipeline-date"
                          className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs" />
                        <input type="time" value={pipelineTime} onChange={(e) => setPipelineTime(e.target.value)}
                          aria-label="Pipeline time"
                          data-testid="pipeline-time"
                          className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs" />
                      </>
                    )}
                    <button type="button" onClick={handleRunPipeline}
                      disabled={pipelineLoading || !pipelineIdea.trim()}
                      data-testid="run-pipeline-button"
                      className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-xs font-bold rounded-lg disabled:opacity-50">
                      {pipelineLoading ? <RefreshCw size={12} className="animate-spin" aria-hidden="true" /> : <Sparkles size={12} aria-hidden="true" />}
                      {pipelineLoading ? 'Running...' : 'Run pipeline'}
                    </button>
                  </div>
                  <p className="text-[10px] text-white/60">
                    Uses your current Platform / Brand profile / Post type / Tones / Length. Skip the manual form - drop an idea, hit Run.
                  </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-4" data-testid="post-generator-panel">
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3 lg:col-span-1">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Setup</h2>
                  <div>
                    <label htmlFor="pp-platform" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Platform</label>
                    <select id="pp-platform" value={postForm.platform} onChange={(e) => setPostForm({ ...postForm, platform: e.target.value })}
                      data-testid="post-platform"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      {postMeta.platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pp-profile" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Brand profile</label>
                    <select id="pp-profile" value={postForm.profile_id} onChange={(e) => setPostForm({ ...postForm, profile_id: e.target.value })}
                      data-testid="post-profile"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="">- No profile -</option>
                      {groupedProfileOptions(profiles)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pp-type" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Post type</label>
                    <select id="pp-type" value={postForm.post_type} onChange={(e) => setPostForm({ ...postForm, post_type: e.target.value })}
                      data-testid="post-type"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      {postMeta.post_types.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pp-framework" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Viral framework</label>
                    <select id="pp-framework" value={postForm.framework} onChange={(e) => setPostForm({ ...postForm, framework: e.target.value })}
                      data-testid="post-framework"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="">- Auto-pick best framework -</option>
                      {postMeta.frameworks.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pp-emo" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Emotional goal</label>
                    <select id="pp-emo" value={postForm.emotional_goal} onChange={(e) => setPostForm({ ...postForm, emotional_goal: e.target.value })}
                      data-testid="post-emotional-goal"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      {postMeta.emotional_targets.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label htmlFor="pp-length" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Length</label>
                      <select id="pp-length" value={postForm.length} onChange={(e) => setPostForm({ ...postForm, length: e.target.value })}
                        data-testid="post-length"
                        className="w-full px-2 py-2 border border-[#E2E8F0] rounded-lg text-xs">
                        <option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="pp-emoji" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Emoji</label>
                      <select id="pp-emoji" value={postForm.emoji_level} onChange={(e) => setPostForm({ ...postForm, emoji_level: e.target.value })}
                        data-testid="post-emoji-level"
                        className="w-full px-2 py-2 border border-[#E2E8F0] rounded-lg text-xs">
                        <option value="">Auto</option><option value="low">Low</option><option value="medium">Med</option><option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="pp-urg" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Urgency</label>
                      <select id="pp-urg" value={postForm.urgency} onChange={(e) => setPostForm({ ...postForm, urgency: e.target.value })}
                        data-testid="post-urgency"
                        className="w-full px-2 py-2 border border-[#E2E8F0] rounded-lg text-xs">
                        <option value="low">Low</option><option value="medium">Med</option><option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Tones</label>
                    <div className="flex flex-wrap gap-1.5" data-testid="post-tones">
                      {TONES.map((t) => (
                        <button key={t} type="button" onClick={() => togglePostTone(t)}
                          data-testid={`post-tone-${t}`}
                          aria-pressed={postForm.tones.includes(t)}
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${postForm.tones.includes(t) ? 'bg-[#0A0F1C] text-white border-[#0A0F1C]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">What's the post about?</h2>
                  <div>
                    <label htmlFor="pp-topic" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Topic *</label>
                    <input id="pp-topic" type="text" value={postForm.topic}
                      onChange={(e) => setPostForm({ ...postForm, topic: e.target.value })}
                      placeholder={`e.g. "Why your kids shouldn't pay for your funeral" or "Spirit Airlines layoffs prove job security is a myth"`}
                      data-testid="post-topic"
                      className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="pp-niche" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Niche (optional)</label>
                      <input id="pp-niche" type="text" value={postForm.niche}
                        onChange={(e) => setPostForm({ ...postForm, niche: e.target.value })}
                        data-testid="post-niche"
                        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                    </div>
                    <div>
                      <label htmlFor="pp-aud" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Audience (optional)</label>
                      <input id="pp-aud" type="text" value={postForm.audience}
                        onChange={(e) => setPostForm({ ...postForm, audience: e.target.value })}
                        data-testid="post-audience"
                        className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="pp-cta" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">CTA goal</label>
                    <input id="pp-cta" type="text" value={postForm.cta_goal}
                      onChange={(e) => setPostForm({ ...postForm, cta_goal: e.target.value })}
                      placeholder='e.g. "book consult", "drive comments", "join email list"'
                      data-testid="post-cta-goal"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <div>
                    <label htmlFor="pp-ctx" className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Extra context (optional)</label>
                    <textarea id="pp-ctx" rows={4} value={postForm.extra_context}
                      onChange={(e) => setPostForm({ ...postForm, extra_context: e.target.value })}
                      placeholder="Story details, statistics, recent events, brand-specific phrasing..."
                      data-testid="post-extra-context"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <button type="button" onClick={handleGeneratePost} disabled={postGenLoading || !postForm.topic}
                    data-testid="generate-post-button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg disabled:opacity-50">
                    {postGenLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {postGenLoading ? 'Writing post...' : 'Generate post'}
                  </button>
                </div>
              </div>
              </>
            )}

            {postSubTab === 'refine' && (
              activePost ? (
                <PostRefineView
                  post={activePost}
                  onCopy={copy}
                  onSelectVariant={handleSelectPostVariant}
                  onHumanize={handlePostHumanize}
                  humanizeLevel={postHumanizeLevel}
                  setHumanizeLevel={setPostHumanizeLevel}
                  onMoreViral={handlePostMoreViral}
                  onMoreEmotional={handlePostMoreEmotional}
                  onShorter={handlePostShorter}
                  onLonger={handlePostLonger}
                  onCompliant={handlePostCompliant}
                  onRewrite={handlePostRewrite}
                  rewriteFeedback={postRewriteFeedback}
                  setRewriteFeedback={setPostRewriteFeedback}
                  transforming={postTransforming}
                  onStatusChange={handlePostStatusChange}
                  onDelete={handleDeletePost}
                  // Round 5C
                  imageStyle={imageStyle} setImageStyle={setImageStyle}
                  imageAspect={imageAspect} setImageAspect={setImageAspect}
                  imageCustomPrompt={imageCustomPrompt} setImageCustomPrompt={setImageCustomPrompt}
                  imageGenLoading={imageGenLoading}
                  onGenerateImage={handleGenerateImage}
                  onDeleteImage={handleDeleteImage}
                  onHostImage={(imgId) => handleHostImage(activePost.post_id, imgId)}
                  imageSuggestions={imageSuggestions}
                  imageSuggestionsLoading={imageSuggestionsLoading}
                  onSuggestImages={handleSuggestImages}
                  scheduleDate={scheduleDate} setScheduleDate={setScheduleDate}
                  scheduleTime={scheduleTime} setScheduleTime={setScheduleTime}
                  scheduleChannel={scheduleChannel} setScheduleChannel={setScheduleChannel}
                  scheduleAutoPublish={scheduleAutoPublish} setScheduleAutoPublish={setScheduleAutoPublish}
                  schedulingLoading={schedulingLoading}
                  onSchedule={handleSchedulePost}
                  onCancelSchedule={handleCancelSchedule}
                  convertTarget={convertTarget} setConvertTarget={setConvertTarget}
                  convertGoal={convertGoal} setConvertGoal={setConvertGoal}
                  convertLoading={convertLoading}
                  onConvertToVideo={handleConvertToVideo}
                  complianceLoading={complianceLoading}
                  onComplianceRecheck={handleComplianceRecheck}
                  schedulerProfiles={schedulerProfiles}
                  onGeneratePlatformHashtags={handlePostPlatformHashtags}
                  generatingPlatformHashtags={isPending('post-platform-hashtags')}
                />
              ) : (
                <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center" data-testid="post-refine-empty">
                  <BookOpen size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                  <p className="text-sm text-[#475569] mt-3">Generate or open a post from the Library to refine it.</p>
                </div>
              )
            )}

            {postSubTab === 'carousels' && (
              <div className="grid lg:grid-cols-3 gap-4" data-testid="carousels-panel">
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Carousel Builder</h2>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Platform</label>
                    <select value={carouselForm.platform} onChange={(e) => setCarouselForm({ ...carouselForm, platform: e.target.value })}
                      data-testid="carousel-platform"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="instagram">Instagram</option><option value="linkedin">LinkedIn</option><option value="facebook">Facebook</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Brand profile</label>
                    <select value={carouselForm.profile_id} onChange={(e) => setCarouselForm({ ...carouselForm, profile_id: e.target.value })}
                      data-testid="carousel-profile"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="">- No profile -</option>
                      {groupedProfileOptions(profiles)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Carousel type</label>
                    <select value={carouselForm.carousel_type} onChange={(e) => setCarouselForm({ ...carouselForm, carousel_type: e.target.value })}
                      data-testid="carousel-type"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      {['educational', 'storytelling', 'tips', 'framework', 'step_by_step', 'myth_busting', 'transformation', 'quote'].map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Number of slides</label>
                    <input type="number" min="3" max="15" value={carouselForm.slide_count}
                      onChange={(e) => setCarouselForm({ ...carouselForm, slide_count: parseInt(e.target.value, 10) || 7 })}
                      data-testid="carousel-slide-count"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Topic *</label>
                    <input type="text" value={carouselForm.topic}
                      onChange={(e) => setCarouselForm({ ...carouselForm, topic: e.target.value })}
                      placeholder="e.g. 5 income streams every entrepreneur should own"
                      data-testid="carousel-topic"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <textarea rows={3} value={carouselForm.extra_context}
                    onChange={(e) => setCarouselForm({ ...carouselForm, extra_context: e.target.value })}
                    aria-label="Carousel extra context"
                    placeholder="Optional context - key points to cover"
                    data-testid="carousel-context"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <button type="button" onClick={handleGenerateCarousel} disabled={carouselLoading || !carouselForm.topic}
                    data-testid="generate-carousel-button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                    {carouselLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {carouselLoading ? 'Building...' : 'Build carousel'}
                  </button>
                  <div className="pt-3 border-t border-[#F1F5F9]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Saved ({carousels.length})</div>
                    <ul className="space-y-1 max-h-60 overflow-auto" data-testid="carousels-list">
                      {carousels.map((c) => (
                        <li key={c.carousel_id} className="flex items-center gap-2 text-xs">
                          <button type="button" onClick={() => setActiveCarousel(c)} data-testid={`open-carousel-${c.carousel_id}`}
                            className="flex-1 text-left p-2 rounded hover:bg-[#F8FAFC]">
                            <div className="font-semibold text-[#0A0F1C] truncate">{c.topic}</div>
                            <div className="text-[#94A3B8]">{c.platform} · {c.carousel_type} · {(c.result?.slides || []).length} slides</div>
                          </button>
                          <button type="button" onClick={() => handleDeleteCarousel(c.carousel_id)} aria-label="Delete carousel" className="text-[#94A3B8] hover:text-red-600">
                            <Trash2 size={12} aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  {activeCarousel ? <CarouselView carousel={activeCarousel} onCopy={copy} /> : (
                    <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                      <BookOpen size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                      <p className="text-sm text-[#475569] mt-3">Generate a carousel or open a saved one.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {postSubTab === 'threads' && (
              <div className="grid lg:grid-cols-3 gap-4" data-testid="threads-panel">
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Thread Generator</h2>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Platform</label>
                    <select value={threadForm.platform} onChange={(e) => setThreadForm({ ...threadForm, platform: e.target.value })}
                      data-testid="thread-platform"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="x_twitter">X / Twitter</option><option value="linkedin">LinkedIn</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Brand profile</label>
                    <select value={threadForm.profile_id} onChange={(e) => setThreadForm({ ...threadForm, profile_id: e.target.value })}
                      data-testid="thread-profile"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      <option value="">- No profile -</option>
                      {groupedProfileOptions(profiles)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Thread type</label>
                    <select value={threadForm.thread_type} onChange={(e) => setThreadForm({ ...threadForm, thread_type: e.target.value })}
                      data-testid="thread-type"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                      {['educational', 'story', 'framework', 'mini_breakdown'].map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Length (posts)</label>
                    <input type="number" min="3" max="20" value={threadForm.length}
                      onChange={(e) => setThreadForm({ ...threadForm, length: parseInt(e.target.value, 10) || 7 })}
                      data-testid="thread-length"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Topic *</label>
                    <input type="text" value={threadForm.topic}
                      onChange={(e) => setThreadForm({ ...threadForm, topic: e.target.value })}
                      placeholder="e.g. The biggest mistakes I see new founders make"
                      data-testid="thread-topic"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <textarea rows={3} value={threadForm.extra_context}
                    onChange={(e) => setThreadForm({ ...threadForm, extra_context: e.target.value })}
                    placeholder="Optional context"
                    aria-label="Thread extra context"
                    data-testid="thread-context"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <button type="button" onClick={handleGenerateThread} disabled={threadLoading || !threadForm.topic}
                    data-testid="generate-thread-button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                    {threadLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {threadLoading ? 'Writing...' : 'Write thread'}
                  </button>
                  <div className="pt-3 border-t border-[#F1F5F9]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-2">Saved ({threads.length})</div>
                    <ul className="space-y-1 max-h-60 overflow-auto" data-testid="threads-list">
                      {threads.map((t) => (
                        <li key={t.thread_id} className="flex items-center gap-2 text-xs">
                          <button type="button" onClick={() => setActiveThread(t)} data-testid={`open-thread-${t.thread_id}`}
                            className="flex-1 text-left p-2 rounded hover:bg-[#F8FAFC]">
                            <div className="font-semibold text-[#0A0F1C] truncate">{t.topic}</div>
                            <div className="text-[#94A3B8]">{t.platform} · {(t.result?.posts || []).length + 2} nodes</div>
                          </button>
                          <button type="button" onClick={() => handleDeleteThread(t.thread_id)} aria-label="Delete thread" className="text-[#94A3B8] hover:text-red-600">
                            <Trash2 size={12} aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  {activeThread ? <ThreadView thread={activeThread} onCopy={copy} /> : (
                    <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                      <Wand2 size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                      <p className="text-sm text-[#475569] mt-3">Write a thread or open a saved one.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {postSubTab === 'remix' && (
              <div className="space-y-3" data-testid="remix-panel">
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Wand2 size={16} className="text-fuchsia-600" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Remix a viral post</h2>
                  </div>
                  <p className="text-xs text-[#64748B]">
                    Paste any post you love (a competitor, a creator, anything). I will rewrite it in your brand voice with your tone shift, without plagiarizing the exact phrasing.
                  </p>
                  <textarea rows={6} value={remixSource} onChange={(e) => setRemixSource(e.target.value)}
                    placeholder="Paste the post you want to remix here..."
                    aria-label="Source post"
                    data-testid="remix-source"
                    className="w-full text-sm px-3 py-2 border border-[#E2E8F0] rounded-lg" />
                  <div className="grid sm:grid-cols-3 gap-2">
                    <select value={remixPlatform} onChange={(e) => setRemixPlatform(e.target.value)}
                      aria-label="Target platform"
                      data-testid="remix-platform"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                      {postMeta.platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <input type="text" value={remixTone} onChange={(e) => setRemixTone(e.target.value)}
                      placeholder="Tone shift: e.g. warmer, edgier, more authority"
                      aria-label="Tone shift"
                      data-testid="remix-tone"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                    <input type="text" value={remixAngle} onChange={(e) => setRemixAngle(e.target.value)}
                      placeholder="Angle shift: e.g. make it personal, drop the listicle"
                      aria-label="Angle shift"
                      data-testid="remix-angle"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                  </div>
                  <button type="button" onClick={handleRemix} disabled={remixLoading || remixSource.trim().length < 40}
                    data-testid="remix-button"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-fuchsia-600 text-white text-sm font-bold rounded-lg disabled:opacity-50">
                    {remixLoading ? <RefreshCw size={13} className="animate-spin" aria-hidden="true" /> : <Wand2 size={13} aria-hidden="true" />}
                    {remixLoading ? 'Remixing...' : 'Remix in my voice'}
                  </button>
                </div>

                {remixVariants.length > 0 && (
                  <div className="grid lg:grid-cols-2 gap-3" data-testid="remix-results">
                    {remixVariants.map((v, i) => (
                      <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A0F1C]">Variant {i + 1}</h3>
                          {(v.viral_scores || {}).overall && (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700 rounded-full">
                              {v.viral_scores.overall} viral
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#0A0F1C] whitespace-pre-wrap">{v.full_post}</p>
                        {v.angle && <p className="text-[10px] text-[#64748B] italic"><strong>Angle:</strong> {v.angle}</p>}
                        <button type="button" onClick={() => copy(v.full_post)}
                          data-testid={`copy-remix-${i}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-full">
                          <Copy size={11} aria-hidden="true" /> Copy
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(postSubTab === 'engagement' || postSubTab === 'community') && (
              <div className="grid lg:grid-cols-3 gap-4" data-testid={`${postSubTab}-panel`}>
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-3">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">
                    {postSubTab === 'engagement' ? 'Engagement Engine' : 'Community Post Engine'}
                  </h2>
                  <p className="text-xs text-[#64748B]">
                    {postSubTab === 'engagement'
                      ? 'Generate polls, this-or-that, controversial takes, debate starters - designed to drive comments.'
                      : 'Generate encouragement, milestones, celebrations, audience appreciation - for churches, nonprofits, memberships.'}
                  </p>
                  <select value={batchForm.platform} onChange={(e) => setBatchForm({ ...batchForm, platform: e.target.value })}
                    data-testid="batch-platform"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                    {postMeta.platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                  <select value={batchForm.profile_id} onChange={(e) => setBatchForm({ ...batchForm, profile_id: e.target.value })}
                    data-testid="batch-profile"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm">
                    <option value="">- No profile -</option>
                    {groupedProfileOptions(profiles)}
                  </select>
                  <input type="text" value={batchForm.topic}
                    onChange={(e) => setBatchForm({ ...batchForm, topic: e.target.value })}
                    placeholder="Topic"
                    aria-label="Batch topic"
                    data-testid="batch-topic"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Number of drafts</label>
                    <input type="number" min="3" max="20" value={batchForm.count}
                      onChange={(e) => setBatchForm({ ...batchForm, count: parseInt(e.target.value, 10) || 6 })}
                      data-testid="batch-count"
                      className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  </div>
                  <textarea rows={3} value={batchForm.extra_context}
                    onChange={(e) => setBatchForm({ ...batchForm, extra_context: e.target.value })}
                    placeholder="Optional context"
                    aria-label="Batch extra context"
                    data-testid="batch-context"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm" />
                  <button type="button" onClick={() => { setBatchMode(postSubTab); handleGenerateBatch(); }} disabled={batchLoading || !batchForm.topic}
                    data-testid={`generate-${postSubTab}-button`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50">
                    {batchLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {batchLoading ? 'Generating...' : `Generate ${postSubTab === 'engagement' ? 'engagement' : 'community'} drafts`}
                  </button>
                </div>
                <div className="lg:col-span-2 space-y-3" data-testid="batch-drafts">
                  {batchDrafts.length === 0 ? (
                    <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-12 text-center">
                      <ListChecks size={32} className="mx-auto text-[#94A3B8]" aria-hidden="true" />
                      <p className="text-sm text-[#475569] mt-3">Fill the form and click Generate to see drafts.</p>
                    </div>
                  ) : batchDrafts.map((d, i) => {
                    const overall = Number((d.viral_scores || {}).overall || 0);
                    return (
                      <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid={`draft-${i}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{d.style}</span>
                            <span className="text-xs text-[#475569] ml-2">{d.title_hint}</span>
                          </div>
                          {overall > 0 && <span className="text-lg font-black" style={{ color: scoreColor(overall) }}>{overall}</span>}
                        </div>
                        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 whitespace-pre-wrap text-sm text-[#0A0F1C]">{d.full_post}</div>
                        {d.engagement_question && <p className="text-xs text-[#475569] italic">→ {d.engagement_question}</p>}
                        {d.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-1">{d.hashtags.map((h, j) => <span key={j} className="text-[10px] px-1.5 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full">{h}</span>)}</div>
                        )}
                        <div className="flex gap-2">
                          <button type="button" onClick={() => copy(d.full_post)} className="text-xs text-[#475569] hover:text-[#1D4ED8] underline">Copy</button>
                          <button type="button" onClick={() => handleSaveDraft(d)} data-testid={`save-draft-${i}`}
                            className="text-xs font-semibold text-[#1D4ED8] hover:underline">Save to Post Library →</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {postSubTab === 'scheduler' && (
              <div className="space-y-3" data-testid="post-scheduler-panel">
                {/* ===== Build my next week - one-click weekly content engine ===== */}
                <div className="bg-gradient-to-br from-fuchsia-600 via-fuchsia-700 to-[#1E2B49] text-white rounded-2xl p-5 space-y-3" data-testid="build-week-panel">
                  <div className="flex items-center gap-2">
                    <Sparkles size={18} className="text-white" aria-hidden="true" />
                    <h2 className="text-base font-bold uppercase tracking-wider">Build my next week</h2>
                    <span className="text-[10px] uppercase tracking-wider text-white/70 ml-auto">brand + best-times + 1 post per day</span>
                  </div>
                  <p className="text-xs text-white/85">
                    One click. Auto-generates 7 platform-rotated posts using your DCA voice, queues each at its platform's best-time-to-post slot, and saves them all to your Library + Scheduler. Ready to review or auto-publish.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-white/85">Days
                      <input type="number" min={3} max={14} value={buildWeekDays}
                        onChange={(e) => setBuildWeekDays(Math.max(3, Math.min(14, Number(e.target.value) || 7)))}
                        aria-label="Days to plan"
                        data-testid="build-week-days"
                        className="w-16 ml-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs" />
                    </label>
                    <button type="button" onClick={handleBuildNextWeek} disabled={buildWeekLoading}
                      data-testid="build-week-button"
                      className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 bg-white text-fuchsia-700 text-sm font-bold rounded-lg disabled:opacity-50">
                      {buildWeekLoading ? <RefreshCw size={13} className="animate-spin" aria-hidden="true" /> : <Sparkles size={13} aria-hidden="true" />}
                      {buildWeekLoading ? 'Building...' : `Build my next ${buildWeekDays} days`}
                    </button>
                  </div>
                  {buildWeekJob && (
                    <div className="bg-white/10 border border-white/20 rounded-lg p-2 text-[11px] space-y-1" data-testid="build-week-progress">
                      <div className="font-semibold">
                        Status: {buildWeekJob.status} ({buildWeekJob.days_done}/{buildWeekJob.days_total})
                      </div>
                      {(buildWeekJob.plan || []).slice(0, 14).map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="font-bold">Day {p.day}</span>
                          <span className="text-white/70 capitalize">{(p.platform || '').replace('_', ' ')}</span>
                          <span className="text-white/50">{(p.publish_at || '').slice(0, 16)}</span>
                          {p.post_id && <span className="text-emerald-200">post {p.post_id.slice(-6)}</span>}
                          {p.error && <span className="text-red-200">err</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ===== Provider eligibility helper ===== */}
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="provider-eligibility">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Which accounts work for auto-posting?</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-[#94A3B8] text-left">
                          <th className="py-1 pr-2">Platform</th>
                          <th className="py-1 pr-2">Personal?</th>
                          <th className="py-1 pr-2">What you need</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Facebook',  'No',  'A Facebook Page (free to create). Meta blocks API posting to personal timelines.'],
                          ['Instagram', 'No',  'Business or Creator account (free conversion in IG app, 60 seconds).'],
                          ['LinkedIn',  'Yes', 'Personal profile OR Company Page (both supported).'],
                          ['X / Twitter', 'Yes', 'Any account works via OAuth.'],
                          ['TikTok',    'Limited', 'TikTok for Business / Developer access required.'],
                          ['Snapchat',  'N/A', 'No public posting API. Use the Snapchat helper below for copy-paste.'],
                          ['YouTube Community', 'Yes', 'Personal channel works via OAuth.'],
                        ].map(([p, allowed, note]) => (
                          <tr key={p} className="border-t border-[#F1F5F9]">
                            <td className="py-1.5 pr-2 font-semibold text-[#0A0F1C]">{p}</td>
                            <td className="py-1.5 pr-2">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${allowed === 'Yes' ? 'bg-emerald-100 text-emerald-700' : allowed === 'No' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{allowed}</span>
                            </td>
                            <td className="py-1.5 pr-2 text-[#475569]">{note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="scheduler-config">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Buffer connection</h2>
                    {schedulerStatus && (
                      <div className={`text-[11px] px-2 py-1 rounded-lg ${schedulerStatus.buffer_configured ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-[#F8FAFC] text-[#475569] border border-[#E2E8F0]'}`}>
                        Status: {schedulerStatus.buffer_configured ? `Connected (${schedulerStatus.buffer_token_masked})` : 'Not connected (posts will schedule internally only)'}
                      </div>
                    )}
                    <input type="password" value={bufferTokenInput} onChange={(e) => setBufferTokenInput(e.target.value)}
                      placeholder="Paste Buffer access token (or leave blank to clear)"
                      aria-label="Buffer access token"
                      data-testid="buffer-token-input"
                      className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                    <div className="flex gap-2 flex-wrap">
                      <button type="button" onClick={handleSaveBufferToken}
                        data-testid="save-buffer-token"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg">
                        <Save size={12} aria-hidden="true" /> Save
                      </button>
                      <button type="button" onClick={() => { setBufferTokenInput(''); handleSaveBufferToken(); }}
                        data-testid="clear-buffer-token"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-lg">
                        Clear
                      </button>
                      <a href="https://buffer.com/developers/api" target="_blank" rel="noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-[#1D4ED8] hover:underline">
                        Get token →
                      </a>
                    </div>
                    <p className="text-[10px] text-[#94A3B8]">
                      Buffer's API is in flux but supported. Without a token, posts still schedule internally; add the token to push to Buffer's auto-publishing queue.
                    </p>
                  </div>

                  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="scheduler-profiles">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Connected profiles</h2>
                    {schedulerProfiles.length === 0 ? (
                      <p className="text-[11px] text-[#94A3B8]">No profiles. Connect Buffer, then click Refresh.</p>
                    ) : (
                      <ul className="space-y-1">
                        {schedulerProfiles.map((p) => (
                          <li key={p.id} className="text-[11px] flex items-center justify-between bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-1">
                            <span><strong>{p.service}</strong> · {p.name}</span>
                            <span className="text-[10px] text-[#94A3B8]">{p.organization}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button type="button" onClick={refreshSchedulerProfiles}
                      data-testid="refresh-profiles"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-lg">
                      <RefreshCw size={11} aria-hidden="true" /> Refresh
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-2" data-testid="scheduler-queue">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Scheduled queue ({schedulerQueue.length})</h2>
                    <button type="button" onClick={refreshSchedulerQueue}
                      data-testid="refresh-queue"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-lg">
                      <RefreshCw size={11} aria-hidden="true" /> Refresh
                    </button>
                  </div>
                  {schedulerQueue.length === 0 ? (
                    <p className="text-[11px] text-[#94A3B8]">Nothing scheduled yet. Open any post in Refine, pick a date, and click Schedule.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {schedulerQueue.map((it) => (
                        <div key={it.schedule_id} className="border border-[#E2E8F0] rounded-lg p-2 text-[11px]" data-testid={`queue-${it.schedule_id}`}>
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <span className="font-semibold text-[#0A0F1C]">
                              {new Date(it.publish_at).toLocaleString()} · {it.platform}
                            </span>
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${it.status === 'scheduled' ? 'bg-amber-50 text-amber-800 border border-amber-200' : it.status === 'published' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]'}`}>{it.status}</span>
                          </div>
                          {it.post_summary && (
                            <div className="text-[10px] text-[#475569] mt-1">
                              <strong>{it.post_summary.topic}</strong> - {it.post_summary.preview}
                            </div>
                          )}
                          {it.buffer_post_id && (<div className="text-[10px] text-emerald-700 mt-0.5">Buffer ID: {it.buffer_post_id}</div>)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ===== Best time to post intelligence ===== */}
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4 space-y-2" data-testid="best-times-panel">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-800">Best time to post</h2>
                    <span className="text-[10px] text-emerald-700">Learns from your Performance data; falls back to industry defaults.</span>
                  </div>
                  {bestTimes ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="best-times-grid">
                      {Object.entries(bestTimes).map(([plat, rec]) => {
                        const wdNames = ['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'];
                        return (
                          <div key={plat} className={`rounded-lg p-2 border ${rec.source === 'your_data' ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-[#E2E8F0]'}`}>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-[#475569] capitalize">{plat.replace('_', ' ')}</div>
                            <div className="text-xs font-semibold text-[#0A0F1C]">
                              {(rec.best_hours || []).map((h) => `${h}:00`).join(', ')}
                            </div>
                            <div className="text-[10px] text-[#94A3B8]">
                              {(rec.best_weekdays || []).map((w) => wdNames[w] || '?').join(' · ')}
                            </div>
                            <div className="text-[9px] text-emerald-700 mt-0.5">
                              {rec.source === 'your_data' ? `Your data (${rec.samples} samples)` : 'Industry default'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-[#94A3B8]">Loading...</p>
                  )}
                </div>

                {/* ===== Snapchat manual-post helper ===== */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 space-y-2" data-testid="snapchat-helper">
                  <div className="flex items-center gap-2">
                    <Camera size={14} className="text-yellow-700" aria-hidden="true" />
                    <h2 className="text-sm font-bold uppercase tracking-wider text-yellow-900">Snapchat helper</h2>
                    <span className="text-[10px] uppercase tracking-wider text-yellow-700 ml-auto">no API, manual post</span>
                  </div>
                  <p className="text-[11px] text-yellow-900">
                    Snapchat has no public posting API, so we cannot auto-post for you. Open any post in Refine, then click the button below to copy text + hashtags ready to paste into Snapchat in under 10 seconds.
                  </p>
                  {activePost ? (
                    <button type="button"
                      onClick={() => {
                        const v = (activePost.variants || [])[activePost.selected_variant_idx || 0] || {};
                        const txt = `${v.full_post || ''}\n\n${(v.hashtags || []).join(' ')}`.trim();
                        navigator.clipboard.writeText(txt).then(() => toast.success('Post copied. Paste into Snapchat now.'));
                      }}
                      data-testid="snap-copy-bundle"
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-yellow-600 text-white rounded-lg">
                      <Copy size={11} aria-hidden="true" /> Copy current post for Snapchat
                    </button>
                  ) : (
                    <p className="text-[11px] text-yellow-700 italic">Open a post in Refine first, then come back to copy it.</p>
                  )}
                </div>

                {/* ===== Native auto-posting targets ===== */}
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-3" data-testid="native-targets-panel">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Native auto-posting ({nativeTargets.length})</h2>
                    <span className="text-[10px] text-[#94A3B8]">Connect via OAuth (one-click) or paste a token manually.</span>
                  </div>

                  {/* One-click OAuth connect */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="oauth-buttons">
                    <button type="button" onClick={() => handleOAuthConnect('facebook')}
                      data-testid="oauth-facebook"
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#1877F2] text-white rounded-lg hover:opacity-90">
                      Connect Facebook
                    </button>
                    <button type="button" onClick={() => handleOAuthConnect('linkedin')}
                      data-testid="oauth-linkedin"
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#0A66C2] text-white rounded-lg hover:opacity-90">
                      Connect LinkedIn
                    </button>
                    <button type="button" onClick={() => handleOAuthConnect('x_twitter')}
                      data-testid="oauth-x"
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90">
                      Connect X
                    </button>
                  </div>
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-[#475569] hover:text-[#0A0F1C]">Or paste a token manually ▾</summary>
                    <div className="grid md:grid-cols-4 gap-2 pt-2">
                      <select value={nativeForm.provider} onChange={(e) => setNativeForm({ ...nativeForm, provider: e.target.value })}
                        aria-label="Provider" data-testid="native-provider"
                        className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                        <option value="facebook">Facebook Page</option>
                        <option value="instagram">Instagram Business</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="x_twitter">X / Twitter</option>
                      </select>
                      <input type="text" value={nativeForm.label} onChange={(e) => setNativeForm({ ...nativeForm, label: e.target.value })}
                        placeholder="Label (e.g. DCA FB Page)"
                        aria-label="Target label" data-testid="native-label"
                        className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                      <input type="text" value={nativeForm.account_id} onChange={(e) => setNativeForm({ ...nativeForm, account_id: e.target.value })}
                        placeholder={nativeForm.provider === 'linkedin' ? 'urn:li:person:xxxxx' : nativeForm.provider === 'facebook' ? 'page_id' : nativeForm.provider === 'instagram' ? 'ig_user_id' : 'twitter_user_id (opt)'}
                        aria-label="Account ID" data-testid="native-account"
                        className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                      <input type="password" value={nativeForm.access_token} onChange={(e) => setNativeForm({ ...nativeForm, access_token: e.target.value })}
                        placeholder="Long-lived access token"
                        aria-label="Access token" data-testid="native-token"
                        className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                    </div>
                    <button type="button" onClick={handleSaveNativeTarget}
                      data-testid="save-native-target"
                      className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#1D4ED8] text-white rounded-lg">
                      <Save size={11} aria-hidden="true" /> Save
                    </button>
                  </details>
                  {nativeTargets.length > 0 && (
                    <ul className="space-y-1" data-testid="native-targets-list">
                      {nativeTargets.map((t) => (
                        <li key={t.target_id} className="flex items-center justify-between text-[11px] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-1.5">
                          <span><strong className="capitalize">{t.provider}</strong> · {t.label} <span className="text-[#94A3B8]">({t.account_id || 'no id'})</span></span>
                          <div className="flex items-center gap-2">
                            {activePost && (
                              <button type="button" onClick={() => handlePublishNow(activePost.post_id, t.target_id)}
                                data-testid={`publish-now-${t.target_id}`}
                                className="px-2 py-0.5 text-[10px] font-bold bg-emerald-600 text-white rounded-full">Publish now</button>
                            )}
                            <button type="button" onClick={() => handleDeleteNativeTarget(t.target_id)}
                              aria-label="Disconnect"
                              data-testid={`delete-native-${t.target_id}`}
                              className="text-red-600 hover:bg-red-50 p-1 rounded-full"><Trash2 size={11} aria-hidden="true" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-[#94A3B8]">
                    FB Page: needs a long-lived page access token + page_id.
                    LinkedIn: OAuth2 user/page token with w_member_social scope.
                    X: OAuth2 user context bearer with tweet.write.
                    IG Business: needs a public image_url in target.extra.image_url plus the page-linked token.
                  </p>
                </div>

                {/* ===== Recurring post templates ===== */}
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 space-y-3" data-testid="recurring-templates-panel">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Recurring posts ({recurringTemplates.length})</h2>
                    <span className="text-[10px] text-[#94A3B8]">"Post this weekly" - auto-generate + publish on a cadence.</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-2">
                    <input type="text" value={recurringForm.name} onChange={(e) => setRecurringForm({ ...recurringForm, name: e.target.value })}
                      placeholder="Name (e.g. Weekly money mindset)"
                      aria-label="Recurring name" data-testid="recurring-name"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                    <select value={recurringForm.cadence} onChange={(e) => setRecurringForm({ ...recurringForm, cadence: e.target.value })}
                      aria-label="Cadence" data-testid="recurring-cadence"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <input type="time" value={recurringForm.time_of_day} onChange={(e) => setRecurringForm({ ...recurringForm, time_of_day: e.target.value })}
                      aria-label="Time of day" data-testid="recurring-time"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-2">
                    <select value={recurringForm.platform} onChange={(e) => setRecurringForm({ ...recurringForm, platform: e.target.value })}
                      aria-label="Platform" data-testid="recurring-platform"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                      {postMeta.platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                    <select value={recurringForm.profile_id} onChange={(e) => setRecurringForm({ ...recurringForm, profile_id: e.target.value })}
                      aria-label="Brand profile" data-testid="recurring-profile"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                      <option value="">- No profile -</option>
                      {groupedProfileOptions(profiles)}
                    </select>
                    <select value={recurringForm.target_id} onChange={(e) => setRecurringForm({ ...recurringForm, target_id: e.target.value })}
                      aria-label="Auto-publish target" data-testid="recurring-target"
                      className="text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg">
                      <option value="">- Generate only (no auto-publish) -</option>
                      {nativeTargets.map((t) => <option key={t.target_id} value={t.target_id}>{t.provider}: {t.label}</option>)}
                    </select>
                  </div>
                  <input type="text" value={recurringForm.topic} onChange={(e) => setRecurringForm({ ...recurringForm, topic: e.target.value })}
                    placeholder="Topic / theme (varied by AI each run)"
                    aria-label="Recurring topic" data-testid="recurring-topic"
                    className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-lg" />
                  <button type="button" onClick={handleCreateRecurring}
                    data-testid="create-recurring"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-fuchsia-600 text-white rounded-lg">
                    <Plus size={11} aria-hidden="true" /> Create template
                  </button>
                  {recurringTemplates.length > 0 && (
                    <ul className="space-y-1" data-testid="recurring-list">
                      {recurringTemplates.map((t) => (
                        <li key={t.template_id} className="flex items-center justify-between flex-wrap gap-2 text-[11px] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-1.5">
                          <div>
                            <strong>{t.name}</strong> · <span className="capitalize">{t.cadence}</span> @ {t.time_of_day} · {t.platform}
                            {t.target_id && <span className="text-emerald-700"> · auto-publish</span>}
                            {!t.active && <span className="text-amber-700"> · paused</span>}
                            {t.run_count > 0 && <span className="text-[#94A3B8]"> · {t.run_count} runs</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => handleRunRecurringNow(t.template_id)}
                              data-testid={`run-recurring-${t.template_id}`}
                              className="px-2 py-0.5 text-[10px] font-bold bg-fuchsia-600 text-white rounded-full">Run now</button>
                            <button type="button" onClick={() => togglePatchRecurring(t.template_id, !t.active)}
                              data-testid={`toggle-recurring-${t.template_id}`}
                              className="px-2 py-0.5 text-[10px] font-bold bg-white border border-[#E2E8F0] text-[#0A0F1C] rounded-full">{t.active ? 'Pause' : 'Resume'}</button>
                            <button type="button" onClick={() => handleDeleteRecurring(t.template_id)}
                              aria-label="Delete recurring"
                              data-testid={`delete-recurring-${t.template_id}`}
                              className="text-red-600 hover:bg-red-50 p-1 rounded-full"><Trash2 size={11} aria-hidden="true" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {postSubTab === 'library' && (
              <UnifiedLibrary
                studio="post"
                api={api}
                profiles={profiles}
                onOpenItem={async (item) => {
                  if (item.kind === 'post') {
                    try {
                      const r = await api.get(`/api/admin/social-studio/posts/${item.id}`);
                      setActivePost(r.data.post);
                      setPostSubTab('refine');
                    } catch (e) {
                      toast.error('Open failed: ' + (e.response?.data?.detail || e.message));
                    }
                  } else if (item.kind === 'carousel') {
                    setPostSubTab('carousels');
                    toast.info('Carousel opened (scroll to find it)');
                  } else if (item.kind === 'thread') {
                    setPostSubTab('threads');
                    toast.info('Thread opened (scroll to find it)');
                  }
                }}
              />
            )}
          </div>
        )}

        {/* ===== Idea & Hook Lab ===== */}
        {tab === 'ideas' && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="ideas-panel">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Hook Lab</h2>
              <p className="text-xs text-[#64748B] -mt-1">Generate scroll-stopping first-line hooks for any topic.</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="hook-platform" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Platform</label>
                  <select
                    id="hook-platform"
                    value={hookForm.platform}
                    onChange={(e) => setHookForm({ ...hookForm, platform: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                    data-testid="hook-platform"
                  >
                    {SINGLE_PLATFORMS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="hook-count" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Number of hooks</label>
                  <input
                    id="hook-count"
                    type="number"
                    min="3"
                    max="20"
                    value={hookForm.count}
                    onChange={(e) => setHookForm({ ...hookForm, count: parseInt(e.target.value, 10) || 8 })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                    data-testid="hook-count"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="hook-niche" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Niche</label>
                <input
                  id="hook-niche"
                  type="text"
                  placeholder="e.g. business strategy, final expense insurance"
                  value={hookForm.niche}
                  onChange={(e) => setHookForm({ ...hookForm, niche: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="hook-niche"
                />
              </div>
              <div>
                <label htmlFor="hook-audience" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Target audience</label>
                <input
                  id="hook-audience"
                  type="text"
                  placeholder="e.g. side-hustlers 25-45, seniors 50-85"
                  value={hookForm.audience}
                  onChange={(e) => setHookForm({ ...hookForm, audience: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="hook-audience"
                />
              </div>
              <div>
                <label htmlFor="hook-topic" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Topic</label>
                <input
                  id="hook-topic"
                  type="text"
                  placeholder="e.g. why 9-5 keeps you broke"
                  value={hookForm.topic}
                  onChange={(e) => setHookForm({ ...hookForm, topic: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="hook-topic"
                />
              </div>
              <button
                type="button"
                onClick={handleGenHooks}
                disabled={hooksLoading}
                data-testid="generate-hooks-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50"
              >
                {hooksLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                Generate hooks
              </button>
              {hooks.length > 0 && (
                <>
                  <ul className="space-y-2 mt-3" data-testid="hooks-list">
                    {hooks.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#0A0F1C] bg-[#F8FAFC] p-2.5 rounded-lg border border-[#E2E8F0]">
                        <span className="font-mono text-[10px] text-[#94A3B8] mt-0.5">#{i + 1}</span>
                        <span className="flex-1">{h}</span>
                        <button
                          type="button"
                          onClick={() => copy(h)}
                          aria-label={`Copy hook ${i + 1}`}
                          className="text-[#64748B] hover:text-[#1D4ED8]"
                        >
                          <Copy size={12} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await api.post('/api/admin/social-studio/hook-lab/save', {
                          platform: hookForm.platform,
                          profile_id: profileId || null,
                          niche: hookForm.niche,
                          audience: '',
                          topic: hookForm.topic || 'Hook batch',
                          items: hooks.map((h, idx) => ({ hook: h, title: `Hook ${idx + 1}` })),
                          kind: 'hook',
                        });
                        toast.success(`Saved ${r.data.saved_count} hooks to Library`);
                      } catch (e) {
                        toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                    data-testid="save-hooks-to-library-button"
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-[#0A0F1C] text-white text-xs font-semibold rounded-lg hover:bg-[#1E293B] w-full justify-center"
                  >
                    <Library size={12} aria-hidden="true" /> Save all hooks to Library
                  </button>
                </>
              )}
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Content Idea Library</h2>
              <p className="text-xs text-[#64748B] -mt-1">Brainstorm a batch of fresh video ideas you can pick from.</p>
              <div>
                <label htmlFor="idea-niche" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Niche</label>
                <input
                  id="idea-niche"
                  type="text"
                  placeholder="e.g. business strategy"
                  value={ideaForm.niche}
                  onChange={(e) => setIdeaForm({ ...ideaForm, niche: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="idea-niche"
                />
              </div>
              <div>
                <label htmlFor="idea-audience" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Target audience</label>
                <input
                  id="idea-audience"
                  type="text"
                  placeholder="e.g. side-hustlers and aspiring entrepreneurs"
                  value={ideaForm.audience}
                  onChange={(e) => setIdeaForm({ ...ideaForm, audience: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="idea-audience"
                />
              </div>
              <div>
                <label htmlFor="idea-count" className="block text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-1">Number of ideas</label>
                <input
                  id="idea-count"
                  type="number"
                  min="3"
                  max="20"
                  value={ideaForm.count}
                  onChange={(e) => setIdeaForm({ ...ideaForm, count: parseInt(e.target.value, 10) || 10 })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                  data-testid="idea-count"
                />
              </div>
              <button
                type="button"
                onClick={handleGenIdeas}
                disabled={ideasLoading}
                data-testid="generate-ideas-button"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg w-full justify-center disabled:opacity-50"
              >
                {ideasLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <ListChecks size={14} aria-hidden="true" />}
                Generate content ideas
              </button>
              {ideas.length > 0 && (
                <>
                  <ul className="space-y-2 mt-3" data-testid="ideas-list">
                    {ideas.map((idea, i) => (
                      <li key={i} className="text-sm bg-[#F8FAFC] p-2.5 rounded-lg border border-[#E2E8F0]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D4ED8]">{idea.archetype}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setTopic(idea.title);
                              setExtraContext(idea.description || '');
                              setTab('generator');
                              toast.success('Loaded into Generator');
                            }}
                            aria-label={`Use idea ${i + 1} in generator`}
                            className="text-[10px] text-[#1D4ED8] font-semibold hover:underline inline-flex items-center"
                          >
                            Use it <ChevronRight size={10} aria-hidden="true" />
                          </button>
                        </div>
                        <div className="font-semibold text-[#0A0F1C]">{idea.title}</div>
                        <div className="text-xs text-[#64748B] mt-0.5">{idea.description}</div>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await api.post('/api/admin/social-studio/hook-lab/save', {
                          platform: 'tiktok',
                          profile_id: profileId || null,
                          niche: ideaForm.niche,
                          audience: ideaForm.audience,
                          topic: `${ideaForm.niche || 'Content'} ideas`,
                          items: ideas.map((it) => ({ title: it.title, hook: it.description, category: it.archetype })),
                          kind: 'idea',
                        });
                        toast.success(`Saved ${r.data.saved_count} ideas to Library`);
                      } catch (e) {
                        toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                    data-testid="save-ideas-to-library-button"
                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-[#0A0F1C] text-white text-xs font-semibold rounded-lg hover:bg-[#1E293B] w-full justify-center"
                  >
                    <Library size={12} aria-hidden="true" /> Save all ideas to Library
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== Library Tab ===== */}
        {tab === 'library' && (
          <UnifiedLibrary
            studio="video"
            api={api}
            profiles={profiles}
            onOpenItem={async (item) => {
              if (item.kind === 'script') {
                try {
                  const r = await api.get(`/api/admin/social-studio/scripts/${item.id}`);
                  setActiveScript(r.data.script);
                  setTab('refine');
                } catch (e) {
                  toast.error('Open failed: ' + (e.response?.data?.detail || e.message));
                }
              } else {
                toast.info(`Item ${item.id} is a ${item.kind} (open from Post Studio)`);
              }
            }}
          />
        )}

        {/* ===== Brand Profiles Tab ===== */}
        {tab === 'brands' && (
          <div data-testid="brands-panel">
            <BrandProfileManager
              onChanged={refreshProfiles}
              onEditingChange={(id) => { setEditingProfileId(id); if (!id) setVoiceResult(null); }}
              renderCardExtras={(p) => (
                <>
                  <BrandReferenceLibrary api={api} profileId={p.profile_id} profileName={p.name} />
                  <ThemeBankEditor api={api} profileId={p.profile_id} />
                </>
              )}
              editorExtras={(
                <div className="border-t border-[#E2E8F0] pt-4 mt-4 space-y-3" data-testid="voice-training-card">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#1D4ED8]" aria-hidden="true" />
                    <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">DCA Post Voice Training</h3>
                  </div>
                  <p className="text-xs text-[#64748B]">Paste 3-15 of <strong>your real best-performing posts</strong> (separated by a blank line or <code>---</code>). The AI reverse-engineers your real voice and auto-tunes this profile.</p>
                  <textarea rows={8} value={voiceForm.posts_text}
                    onChange={(e) => setVoiceForm({ ...voiceForm, posts_text: e.target.value })}
                    placeholder={`Post 1 text here...\n\n---\n\nPost 2 text here...\n\n---\n\nPost 3 text here...`}
                    aria-label="Real posts to learn from"
                    data-testid="voice-posts-input"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-xs font-mono" />
                  <input type="text" value={voiceForm.notes}
                    onChange={(e) => setVoiceForm({ ...voiceForm, notes: e.target.value })}
                    placeholder="Optional notes (e.g. 'these all hit 10k+ views')"
                    aria-label="Voice training notes"
                    data-testid="voice-notes"
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-xs" />
                  <button type="button" onClick={handleVoiceTrain} disabled={voiceLoading}
                    data-testid="voice-train-button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0F1C] text-white text-sm font-semibold rounded-lg disabled:opacity-50">
                    {voiceLoading ? <RefreshCw size={14} className="animate-spin" aria-hidden="true" /> : <Sparkles size={14} aria-hidden="true" />}
                    {voiceLoading ? 'Analysing your voice...' : 'Train brand voice from posts'}
                  </button>
                  {voiceResult?.analysis && (
                    <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid="voice-result">
                      {voiceResult.analysis.summary && (
                        <p className="text-xs text-[#0A0F1C]"><strong>Voice summary:</strong> {voiceResult.analysis.summary}</p>
                      )}
                      <div className="grid sm:grid-cols-2 gap-2 text-[10px] text-[#475569]">
                        {Object.entries(voiceResult.applied || {}).map(([k, v]) => (
                          <div key={k} className="bg-white border border-[#E2E8F0] rounded p-1.5">
                            <strong className="text-emerald-700">+</strong> {k}: <strong>{typeof v === 'number' ? `${v} added` : 'updated'}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
          </div>
        )}
      </div>
  );
  if (embedded) return inner;
  return <AdminLayout title="Social Media Viral Studio" data-testid="social-studio-page">{inner}</AdminLayout>;
}

// ===== Small UI helpers =====
function ScriptSection({ label, body, color }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color }}>{label}</div>
      <div className="text-sm text-[#0A0F1C] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 leading-relaxed">{body}</div>
    </div>
  );
}

function Pillbox({ label, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{label}</div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-xs text-[#475569] before:content-['•'] before:mr-2 before:text-[#94A3B8]">{it}</li>
        ))}
      </ul>
    </div>
  );
}

function buildFullScriptText(v) {
  if (!v) return '';
  if (v._unparsed) return v.full_script || '';
  const parts = [
    v.title ? `# ${v.title}` : '',
    v.hook ? `\n[HOOK 0-3s]\n${v.hook}` : '',
    v.problem ? `\n[PROBLEM 3-13s]\n${v.problem}` : '',
    v.solution ? `\n[SOLUTION 13-50s]\n${v.solution}` : '',
    v.cta ? `\n[CTA 50-60s]\n${v.cta}` : '',
    v.on_screen_text?.length ? `\n\nON-SCREEN TEXT:\n${v.on_screen_text.map((s) => '• ' + s).join('\n')}` : '',
    v.b_roll_suggestions?.length ? `\n\nB-ROLL:\n${v.b_roll_suggestions.map((s) => '• ' + s).join('\n')}` : '',
    v.caption ? `\n\nCAPTION:\n${v.caption}` : '',
    v.hashtags?.length ? `\n\n${v.hashtags.join(' ')}` : '',
  ].filter(Boolean).join('\n');
  return parts.trim();
}

// Just the spoken words (hook → problem → solution → CTA) for a teleprompter.
function buildTeleprompterText(v) {
  if (!v) return '';
  if (v._unparsed) return v.full_script || '';
  return [v.hook, v.problem, v.solution, v.cta].filter(Boolean).join('\n\n').trim();
}

// ===== Viral Score Card =====
const SCORE_DIMS = [
  { key: 'hook_strength', label: 'Hook strength' },
  { key: 'curiosity', label: 'Curiosity' },
  { key: 'emotional_trigger', label: 'Emotional trigger' },
  { key: 'retention', label: 'Retention' },
  { key: 'clarity', label: 'Clarity' },
  { key: 'cta_strength', label: 'CTA strength' },
  { key: 'shareability', label: 'Shareability' },
  { key: 'comment_potential', label: 'Comment potential' },
  { key: 'platform_fit', label: 'Platform fit' },
];

function scoreColor(score) {
  if (score >= 85) return '#059669';
  if (score >= 70) return '#1D4ED8';
  if (score >= 50) return '#D97706';
  return '#DC2626';
}

function ViralScoreCard({ scores }) {
  const overall = Number(scores.overall || 0);
  const explanation = scores.explanation || {};
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="viral-score-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gauge size={16} className="text-[#1D4ED8]" aria-hidden="true" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Viral score</h2>
        </div>
        <div className="flex items-baseline gap-1" data-testid="viral-score-overall">
          <span className="text-4xl font-black" style={{ color: scoreColor(overall) }}>{overall}</span>
          <span className="text-xs font-semibold text-[#94A3B8]">/100</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
        {SCORE_DIMS.map((d) => {
          const v = Number(scores[d.key] || 0);
          return (
            <div key={d.key} data-testid={`viral-score-${d.key}`}>
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-[#475569] font-medium">{d.label}</span>
                <span className="font-bold" style={{ color: scoreColor(v) }}>{v}</span>
              </div>
              <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, v))}%`, backgroundColor: scoreColor(v) }} />
              </div>
            </div>
          );
        })}
      </div>
      {(explanation.what_is_working || explanation.what_needs_improvement || explanation.biggest_weakness) && (
        <div className="space-y-2 pt-3 border-t border-[#F1F5F9]">
          {explanation.what_is_working && (
            <div className="text-xs"><strong className="text-emerald-700">What's working:</strong> <span className="text-[#475569]">{explanation.what_is_working}</span></div>
          )}
          {explanation.what_needs_improvement && (
            <div className="text-xs"><strong className="text-amber-700">Needs work:</strong> <span className="text-[#475569]">{explanation.what_needs_improvement}</span></div>
          )}
          {explanation.biggest_weakness && (
            <div className="text-xs"><strong className="text-red-700">Biggest weakness:</strong> <span className="text-[#475569]">{explanation.biggest_weakness}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Compliance Card =====
function ComplianceCard({ compliance, onRemoveFlags, removing }) {
  const risk = (compliance.risk_level || 'low').toLowerCase();
  const riskMeta = {
    high: { color: '#DC2626', bg: 'bg-red-50', border: 'border-red-200', icon: ShieldAlert },
    medium: { color: '#D97706', bg: 'bg-amber-50', border: 'border-amber-200', icon: ShieldAlert },
    low: { color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: ShieldCheck },
  }[risk] || { color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: ShieldCheck };
  const Icon = riskMeta.icon;
  return (
    <div className={`border ${riskMeta.border} ${riskMeta.bg} rounded-2xl p-5 space-y-3`} data-testid="compliance-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon size={16} style={{ color: riskMeta.color }} aria-hidden="true" />
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: riskMeta.color }}>Compliance</h2>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: 'white', color: riskMeta.color, border: `1px solid ${riskMeta.color}` }} data-testid="compliance-risk">
          {risk} risk · {compliance.category || 'general'}
        </span>
      </div>
      {compliance.flagged_phrases?.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Flagged phrases</div>
          <div className="flex flex-wrap gap-1.5" data-testid="compliance-flags">
            {compliance.flagged_phrases.map((p, i) => (
              <span key={i} className="text-xs bg-white text-red-700 border border-red-200 px-2 py-0.5 rounded-full">{p}</span>
            ))}
          </div>
          {onRemoveFlags && (
            <button
              type="button"
              onClick={onRemoveFlags}
              disabled={removing}
              data-testid="remove-flags-button"
              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-white text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              <ShieldCheck size={12} aria-hidden="true" /> Remove flagged phrases (apply safer rewrites)
            </button>
          )}
        </div>
      )}
      {compliance.safer_rewrites?.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Safer rewrites</div>
          <ul className="space-y-1" data-testid="compliance-rewrites">
            {compliance.safer_rewrites.map((r, i) => (
              <li key={i} className="text-xs text-[#0A0F1C] bg-white border border-[#E2E8F0] rounded-lg px-2 py-1">{r}</li>
            ))}
          </ul>
        </div>
      )}
      {compliance.suggested_disclaimer && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#475569] mb-1">Suggested disclaimer</div>
          <p className="text-xs text-[#0A0F1C] bg-white border border-[#E2E8F0] rounded-lg p-2" data-testid="compliance-disclaimer">{compliance.suggested_disclaimer}</p>
        </div>
      )}
    </div>
  );
}

// ===== Delivery Coach Card =====
function DeliveryCoachCard({ coach }) {
  const lists = [
    { key: 'emphasize_words', label: 'Emphasize words' },
    { key: 'suggested_pauses', label: 'Suggested pauses' },
    { key: 'zoom_cues', label: 'Zoom cues' },
    { key: 'cut_cues', label: 'Cut cues' },
    { key: 'caption_cues', label: 'Caption cues' },
    { key: 'b_roll', label: 'B-roll' },
    { key: 'props', label: 'Props' },
    { key: 'gestures', label: 'Gestures' },
  ];
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="delivery-coach-card">
      <div className="flex items-center gap-2">
        <Camera size={16} className="text-[#1D4ED8]" aria-hidden="true" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Delivery Coach</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {coach.energy_level && (
          <div data-testid="coach-energy">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Energy</div>
            <div className="text-sm text-[#0A0F1C] font-semibold">{coach.energy_level}</div>
          </div>
        )}
        {coach.facial_expression && (
          <div data-testid="coach-expression">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Facial expression</div>
            <div className="text-sm text-[#0A0F1C]">{coach.facial_expression}</div>
          </div>
        )}
        {coach.background && (
          <div data-testid="coach-background">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Background</div>
            <div className="text-sm text-[#0A0F1C]">{coach.background}</div>
          </div>
        )}
        {coach.opening_frame && (
          <div data-testid="coach-opening-frame">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Opening frame</div>
            <div className="text-sm text-[#0A0F1C]">{coach.opening_frame}</div>
          </div>
        )}
        {coach.ending_frame && (
          <div data-testid="coach-ending-frame">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Ending frame</div>
            <div className="text-sm text-[#0A0F1C]">{coach.ending_frame}</div>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
        {lists.map((l) => (
          coach[l.key]?.length > 0 && (
            <div key={l.key} data-testid={`coach-${l.key}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{l.label}</div>
              <ul className="space-y-0.5">
                {coach[l.key].map((it, i) => (
                  <li key={i} className="text-xs text-[#475569] before:content-['•'] before:mr-1.5 before:text-[#94A3B8]">{it}</li>
                ))}
              </ul>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ===== Captions / SEO / Comments Pack =====
function CaptionsSeoCard({ variant, onCopy, onGeneratePlatformCaptions, generatingCaptions }) {
  const fields = [
    { key: 'short_caption', label: 'Short caption' },
    { key: 'seo_caption', label: 'SEO caption' },
    { key: 'video_title', label: 'Video title (YT Shorts)' },
    { key: 'thumbnail_text', label: 'Thumbnail text' },
    { key: 'pinned_comment', label: 'Pinned comment' },
    { key: 'cta_comment_prompt', label: 'CTA comment prompt' },
    { key: 'first_reply', label: 'Creator first reply' },
  ];
  const visible = fields.filter((f) => variant[f.key]);
  const keywords = variant.search_keywords || [];
  const pcaps = variant.platform_captions;
  if (!visible.length && !keywords.length && !pcaps && !onGeneratePlatformCaptions) return null;
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="captions-seo-card">
      <div className="flex items-center gap-2">
        <BookOpen size={16} className="text-[#1D4ED8]" aria-hidden="true" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Captions, SEO & comments</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {visible.map((f) => (
          <div key={f.key} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5" data-testid={`pack-${f.key}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{f.label}</span>
              <button
                type="button"
                onClick={() => onCopy(variant[f.key])}
                aria-label={`Copy ${f.label}`}
                className="text-[#64748B] hover:text-[#1D4ED8]"
              >
                <Copy size={11} aria-hidden="true" />
              </button>
            </div>
            <div className="text-xs text-[#0A0F1C] whitespace-pre-wrap">{variant[f.key]}</div>
          </div>
        ))}
      </div>
      {keywords.length > 0 && (
        <div data-testid="pack-search-keywords">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Search keywords</div>
          <div className="flex flex-wrap gap-1.5">
            {keywords.map((k, i) => (
              <span key={i} className="text-xs px-2 py-0.5 bg-[#F1F5F9] text-[#475569] rounded-full">{k}</span>
            ))}
          </div>
        </div>
      )}
      {onGeneratePlatformCaptions && !pcaps && (
        <button
          type="button"
          onClick={onGeneratePlatformCaptions}
          disabled={generatingCaptions}
          data-testid="generate-platform-captions-button"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE] rounded-lg hover:bg-[#DBEAFE] disabled:opacity-50"
        >
          <BookOpen size={12} aria-hidden="true" /> {generatingCaptions ? 'Generating…' : 'Generate per-platform captions (TikTok · IG · FB · YT)'}
        </button>
      )}
      {pcaps && (
        <div data-testid="platform-captions">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5">Per-platform captions</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[['tiktok', 'TikTok'], ['instagram_reels', 'Instagram Reels'], ['facebook_reels', 'Facebook Reels'], ['youtube_shorts', 'YouTube Shorts']].map(([k, lbl]) => {
              const pc = pcaps[k];
              if (!pc || (!pc.caption && !(pc.hashtags || []).length)) return null;
              const text = [pc.caption, (pc.hashtags || []).join(' ')].filter(Boolean).join('\n\n');
              return (
                <div key={k} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5" data-testid={`platform-caption-${k}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{lbl}</span>
                    <div className="flex items-center gap-2">
                      {(pc.hashtags || []).length > 0 && (
                        <button type="button" onClick={() => onCopy((pc.hashtags || []).join(' '))} data-testid={`copy-hashtags-${k}`} aria-label={`Copy ${lbl} hashtags`} className="text-[10px] font-bold text-[#1D4ED8] hover:underline">Copy #tags</button>
                      )}
                      <button type="button" onClick={() => onCopy(text)} aria-label={`Copy ${lbl} caption`} className="text-[#64748B] hover:text-[#1D4ED8]"><Copy size={11} aria-hidden="true" /></button>
                    </div>
                  </div>
                  {pc.caption && <div className="text-xs text-[#0A0F1C] whitespace-pre-wrap">{pc.caption}</div>}
                  {(pc.hashtags || []).length > 0 && <div className="text-[11px] text-[#1D4ED8] mt-1">{pc.hashtags.join(' ')}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


